import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Radio,
  RadioGroup,
  Spinner,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
  type RadioGroupOnChangeData,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import type Decimal from 'decimal.js';
import {
  PURCHASE_TYPES,
  REJECTION_SECTION_LABELS,
  formatDate,
  formatDateTime,
  formatNetPrice,
  formatTime,
  formatTotal,
  parseRejectionSection,
  submitRequestSchema,
  type DraftRequestInput,
  type PurchaseType,
  type RejectionSection,
  type RequestStatus,
} from '@pr-app/shared';
import {
  getCompanies,
  getCostCentres,
  getLineOfBusiness,
  getProcurementCategories,
  getProjects,
  getUnits,
  getVendors,
  getMainAccounts,
  getProjectCategories,
  getSalesTaxGroups,
  getWarehouses,
  type CompanyRow,
  type DimensionRow,
  type ProcurementCategoryRow,
  type ProjectRow,
  type UnitRow,
  type VendorRow,
  type MainAccountRow,
  type ProjectCategoryRow,
  type SalesTaxGroupRow,
  type WarehouseRow,
} from '../api/masterdata';
import { ApiError } from '../api/client';
import { saveReviewerEdits } from '../api/review';
import {
  getRequest,
  saveDraft,
  submitRequest,
  type RequestAuditEntry,
  type RequestDetail,
} from '../api/requests';
import { AttachmentsSection } from './AttachmentsSection';
import type { Attachment } from '../api/attachments';
import {
  ReviewSummary,
  type ReviewDetailField,
  type ReviewLine,
  type ReviewSummaryIssue,
} from './ReviewSummary';
import { DataCard, DataCardList } from '../components/DataCardList';
import { EmptyState } from '../components/EmptyState';
import { fromDateOnly, toDateOnly } from '../lib/dateOnly';
import { salesTaxLabel } from '../lib/salesTaxLabel';
import {
  categoryKey,
  categoryLabel,
  categoryMatches,
  resolveCategoryLabel,
  dimensionKey,
  dimensionLabel,
  resolveDependentLabel,
  warehouseKey,
  warehouseLabel,
} from '../lib/masterDataLabels';
import {
  NET_PRICE_PATTERN,
  QUANTITY_PATTERN,
  computeLineTotal,
  sumLineTotals,
} from '../lib/totals';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import { SPACE, SPACE_X } from '../styles/scale';
import { useLayoutStyles, useSurfaceStyles } from '../styles/surfaces';
import { ActionBar } from '../components/ActionBar';
import { FilterableCombobox, compareLabels, firstInOrder } from '../components/FilterableCombobox';
import { codeLabel } from '../lib/codeLabel';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { StatusBadge } from '../components/StatusBadge';

/**
 * SOW §4.2 — Header form (R2/R2a/R2c/R2d, #24/#25/#29): all 11 header fields —
 * purchase type, conditional Project ID, company code, the three dependent
 * dimensions it scopes, vendor search with read-only payment-terms/currency
 * autofill, term start/end dates, delivery date, and the two notes fields.
 *
 * The line-items section below is #26's slice only: procurement categories
 * and units fetched once and shared across every row (not per row). The full
 * 8-field line table — expander, inline validation, computed totals — is #30.
 *
 * R2d note 4 / behaviour rule 8 — "changing vendor warns when lines exist and
 * currency differs" was deferred here until draft-save/lines existed on this
 * form; both do now, so `vendorCurrencyWarning` (#160) implements it: picking
 * a vendor whose currency differs from the previous one, with at least one
 * line already carrying a unit price, shows a dismissible warning rather than
 * silently leaving prices priced in a currency nothing on screen still names.
 */

const VENDOR_SEARCH_DEBOUNCE_MS = 300;
const VENDOR_SEARCH_MIN_CHARS = 2;
/** Wireframe S3a — D365-unreachable fields recover on their own, not just via the manual Retry button. */
export const AUTO_RETRY_DELAY_MS = 8000;
/** Wireframe R7 — auto-save fires on blur and on this interval while the draft is dirty, never per keystroke. */
export const AUTOSAVE_INTERVAL_MS = 30000;
/** K1's last-resort focus destination when the form opens with nothing to fix. */
const HEADING_ELEMENT_ID = 'request-form-heading';
/** R2 note 6 — matches `submitRequestSchema`'s `internalNotes`/`notesForVendor` `.max(2000)`. */
const NOTES_MAX_LENGTH = 2000;

/** Retries automatically while a D365-backed control sits in its error state (wireframe S3a). */
export function useAutoRetry(phase: string, retry: () => void): void {
  useEffect(() => {
    if (phase !== 'error') return undefined;
    const timer = setTimeout(retry, AUTO_RETRY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [phase, retry]);
}

type Rows<T> =
  { phase: 'disabled' } | { phase: 'loading' } | { phase: 'ready'; rows: T[] } | { phase: 'error' };

/**
 * The page's own styles (#202).
 *
 * Everything that used to be a hand-rolled card, footer, banner or heading in
 * here is a shared primitive now — `Section` owns the five Details cards and
 * the Line items / History blocks, `PageHeader` owns the title row, `ActionBar`
 * owns all four wizard footers, and `useSurfaceStyles`/`useLayoutStyles` own
 * every surface and column. What is left below is only what is genuinely local
 * to this form, and every value comes from `SPACE`/`SPACE_X`/`tokens`.
 */
const useStyles = makeStyles({
  /**
   * The "← Back" control. It has no margin of its own — the page's own
   * `useLayoutStyles().stack` owns the vertical rhythm — but it must not
   * stretch to the full width of that flex column, hence `alignSelf`.
   */
  backButton: { alignSelf: 'flex-start' },

  /**
   * The step tabs ("1. Details … 4. Review & submit") are ~500px wide, so on a phone a bare
   * TabList set the PAGE's width and the whole form scrolled sideways. Wide content scrolls
   * inside its own row, never the page: `maxWidth` lets the row shrink below its content,
   * and `overflowX` then takes over.
   */
  stepRail: { maxWidth: '100%', overflowX: 'auto' },

  /** Stacked form fields inside one card — the `m` step of the spacing scale. */
  cardStack: { display: 'flex', flexDirection: 'column', gap: SPACE.m },

  // A row of fields that lays out side by side on desktop (R2's "three
  // across" for Financial dimensions/Dates, "two across" for Notes) and
  // collapses to one column below the mobile breakpoint via
  // `responsive.singleColumn`, same rule as the rest of this page.
  fieldRow: { display: 'flex', gap: SPACE_X.l },
  // Equal-width columns regardless of a Field's own intrinsic content width —
  // `flexBasis: 0` plus `minWidth: 0` lets a long combobox value or hint text
  // wrap/truncate instead of forcing the row wider than its card.
  //
  // The two descendant rules are load-bearing now that the card is capped at
  // the 720px measure rather than filling the whole canvas: Fluent's
  // `Combobox` and `Input` both carry an intrinsic `min-width` of 250px, so
  // three of them across a measured card (688px of content box) overflowed it
  // by ~90px — measured in a real browser at 1440px, invisible to jsdom. The
  // COLUMN owns the width here; the control fills whatever it is given.
  fieldCol: {
    flexGrow: 1,
    flexBasis: '0',
    minWidth: '0',
    '& .fui-Combobox': { minWidth: '0' },
    '& .fui-Input': { minWidth: '0' },
  },

  /** De-emphasis is a foreground token, never `opacity`. */
  hint: { marginTop: SPACE.xs, color: tokens.colorNeutralForeground2 },
  // R2 note 6 — a live `0 / 2000` counter under each notes field, matching
  // `submitRequestSchema`'s own `.max(2000)` (packages/shared/src/schemas.ts).
  charCount: { marginTop: SPACE.xs, color: tokens.colorNeutralForeground2, textAlign: 'right' },
  changedHint: { marginTop: SPACE.xs, color: tokens.colorBrandForeground1 },
  errorText: { marginTop: SPACE.xs, color: tokens.colorPaletteRedForeground1 },

  /** The line-items block inside its `Section`: table (or cards), then totals, then Add line. */
  lineItemsStack: { display: 'flex', flexDirection: 'column', gap: SPACE.m },
  // `tableLayout: 'auto'` (below, on the `<Table>` itself) lets a column grow
  // for a long value instead of overlapping its neighbour — but an `auto`
  // table has no ceiling of its own, so a genuinely extreme amount could
  // grow the table past this container's width. Without a scroll boundary
  // scoped to the table, that overflow bleeds past the card's own themed
  // background into whatever's behind it — unstyled `<body>`, which reads as
  // a stray white panel in dark/high-contrast (the same class of bug as log
  // entry 23, just from table overflow instead of a portal). This is the one
  // deliberate exception to "the tab canvas never scrolls sideways" (#30's
  // own docblock): the scroll is scoped to the table, never the page, and
  // only engages for a value wide enough to need it.
  lineItemsTableScroll: {
    width: '100%',
    overflowX: 'auto',
    // Total and Actions are pinned to the right edge (`stickyTotal`/`stickyActions`). Without this,
    // scrolling or focusing a field just left of them (Sales tax) can park it UNDER the pinned
    // columns. 168px (Total) + 96px (Actions) + a little air.
    scrollPaddingRight: '280px',
  },
  // Explicit `width` (not `minWidth`) on the Delivery date/Total/Procurement
  // category/Unit header cells — Fluent's `Table` defaults to
  // `table-layout: fixed` (every `fui-Table`), and a fixed table sizes every
  // column from the FIRST row's own declared widths, never from later rows'
  // content. `minWidth` on a cell deep in the body has no effect on that
  // first-row-driven allocation, which is why a long formatted Total rendered
  // flush against Delivery date instead of pushing it wider — the column was
  // never actually reserved.
  //
  // Procurement category/Unit need the same treatment for a different reason
  // (#213): with no declared width, "Procurement category" wrapping onto two
  // lines was enough to pin that column to ~104px in a real browser —
  // narrower than the `Combobox` trigger it holds needs, which is what let
  // the trigger's input (and its expand-icon chevron riding along beside it)
  // render outside its own cell instead of centered inside it. Switching the
  // table to `table-layout: auto` was tried first and measured WORSE — auto
  // layout sizes a column from its content's own preferred width, and this
  // column's content (a `width: 100%, min-width: 0` `Combobox`, deliberately
  // shrinkable so it never overflows) has almost no preferred width of its
  // own to claim, so auto layout gave it even less room than fixed did.
  // Reserving real space explicitly is what Delivery date/Total already do,
  // and is what actually fixes the chevron. 160/200px are legible for a
  // truncated trigger value; the full label is what the widened popup below
  // is for. All four are on the 4px grid the project mandates.
  //
  // 192px, not a rounder 160px, for Delivery date: Fluent's `DatePicker`
  // renders its `<input>` at a fixed intrinsic 168px (`box-sizing:
  // border-box`), which does not shrink to fit a narrower container. A
  // column narrower than 168px-plus-the-cell's-own-16px-padding pushes that
  // input past its own cell's right edge before the Total column even
  // starts.
  // 160px for every date column (Delivery, Start, End). The row now carries three of them, so the
  // 192px a default-width Fluent `DatePicker` wants (its input is a fixed ~168px) would alone eat
  // 576px; `dateCell` below lets the input shrink to its cell instead.
  lineItemsDeliveryDateHeader: { width: '160px' },
  // EVERY column in this fixed-layout table needs a declared width: a column with none is handed
  // whatever is left after the others, and once Start/End/Sales tax were added that was nothing —
  // the Item description column collapsed to zero and the Actions column vanished. Description
  // keeps a usable width; the table scrolls inside its own container before it squeezes it.
  lineItemsDescriptionHeader: { width: '200px' },
  lineItemsActionsHeader: { width: '80px' },
  // Total and Actions stay pinned to the right edge of the scroll container: with eleven columns
  // the table is wider than the canvas, and the figure a person checks (the line Total) and the
  // row menu must not sit behind a sideways scroll. Offsets are the declared width plus the cell's
  // own 16px of padding (Actions 80+16, so Total pins just left of it).
  stickyActions: {
    position: 'sticky',
    right: '0px',
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  stickyTotal: {
    position: 'sticky',
    right: '96px',
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `-1px 0 0 ${tokens.colorNeutralStroke2}`,
  },
  lineItemsTotalHeader: { width: '152px' },
  // Reserved just enough to show the trigger's placeholder/value and its
  // chevron without overflowing (#213) — not enough to show a full category
  // label uncropped, which is what the widened popup below is for instead.
  // Kept modest deliberately: this table already runs tight between the
  // mobile card breakpoint (700px) and comfortable desktop widths, and a
  // wider reservation here measurably pushed a PRE-EXISTING squeeze at ~768px
  // (present on `development` before this change too) from "columns overlap
  // in place" to "the row genuinely overflows and scrolls" — arguably no
  // worse, but not this ticket's problem to solve, so the reservation stays
  // as small as still fixes the chevron.
  lineItemsCategoryHeader: { width: '150px' },
  lineItemsUnitHeader: { width: '112px' },
  // Quantity and Unit price hold a short number each, so they are kept narrow — the room goes to
  // the date and sales-tax columns the table gained.
  lineItemsQuantityHeader: { width: '96px' },
  lineItemsNetPriceHeader: { width: '112px' },
  lineItemsSalesTaxHeader: { width: '160px' },
  // `minWidth: '0'` alone only reaches the `Combobox`/`Input` ROOT — the class
  // is passed straight in as `className`, which Fluent merges onto that root
  // element. `fui-Combobox` keeps its own `min-width: 250px` on the ROOT
  // (overridden here), but the root's inner `fui-Combobox__input` grid item
  // carries an independent intrinsic minimum the root override never reaches:
  // a CSS grid item's default minimum is its own min-content size, not its
  // container's. Once the column shrank the root below that minimum (#213,
  // before `lineItemsCategoryHeader`/`lineItemsUnitHeader` reserved it real
  // space), the input rendered 62px wider than its own
  // root and dragged the expand-icon chevron along with it, off the right
  // edge of the visible control entirely — measured in a real browser, not
  // inferred. The nested override is what actually lets the input (and the
  // chevron riding the grid track after it) shrink to fit.
  cellInput: { width: '100%', minWidth: '0', '& .fui-Combobox__input': { minWidth: '0' } },
  // The mobile line-edit sheet (#53): one field per row, never two across.
  sheetFields: { display: 'flex', flexDirection: 'column', gap: SPACE.l },
  /** Wireframe X1: money compares by eye only if the figures are the same width. */
  numeric: { fontVariantNumeric: 'tabular-nums' },
  numericCell: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  /**
   * Center alignment, on every line-items cell AND its header (#213) — same
   * treatment #211/PR #212 gave the Dashboard/Reviewer Queue/Admin tables via
   * `DataTable`'s `alignCenter`. This table predates `DataTable` and is raw
   * markup, so the equivalent is applied locally rather than reused. Same
   * descendant-rule shape as `DataTable`'s own `alignCenter`:
   * `TableHeaderCell` puts its children inside a flex
   * `.fui-TableHeaderCell__button` div, so `text-align` alone moves the `<td>`
   * and leaves the `<th>` looking un-aligned beside it.
   */
  alignCenter: {
    textAlign: 'center',
    '& .fui-TableHeaderCell__button': { justifyContent: 'center' },
  },
  /**
   * The widened popup for the Procurement category/Unit dropdowns (#213):
   * Fluent's `Listbox` defaults to a 160px `min-width`, matched to its
   * trigger's own width rather than its content's — so a label like
   * "44101603 — Paper shredding machines or accessories" wrapped across three
   * lines, hard to scan. Passed via the `Combobox`'s own `listbox` slot prop
   * rather than a descendant selector: the listbox is portaled to the
   * document body on open, so it is not a DOM descendant of anything this
   * component's own generated class could reach. `width: 'max-content'` lets
   * a short label (a unit like "EA") stay narrow instead of stretching to the
   * cap; `maxWidth` keeps a pathological label from spanning the viewport —
   * it is an overlay positioned by Fluent's own floating-ui, so it never
   * affects page layout or causes the page itself to scroll sideways.
   */
  wideListboxPopup: { minWidth: '420px', width: 'max-content', maxWidth: '480px' },
  // A large formatted amount ("1,234,567.00") was visually bleeding into the
  // neighbouring Delivery date cell — the Total column reserved no width of
  // its own, so its `nowrap` text overflowed sideways once the number grew
  // past whatever the browser happened to lay the column out at. `minWidth`
  // makes the table give the column real room instead of shrinking it to fit
  // a short example value; `whiteSpace: nowrap` keeps the amount from
  // wrapping mid-number now that it has room not to. Emphasis is the
  // `Body1Strong` preset inside the cell, never a `fontWeight` declaration.
  //
  // `justifyContent`, not `textAlign` (#213): `TableCellLayout` is a flex row
  // (`display: flex`), and a flex container's own box ignores an ancestor's
  // — or its own — `text-align`, which only repositions inline content.
  // `justifyContent` is the flex equivalent, same shape as `primaryCell` in
  // `Dashboard.tsx` doing the same swap for a flex COLUMN via `alignItems`.
  lineTotalCell: {
    justifyContent: 'center',
    fontVariantNumeric: 'tabular-nums',
    minWidth: '112px',
    whiteSpace: 'nowrap',
  },
  // The Delivery date cell sits directly beside Total — reserving its own
  // minimum width keeps the DatePicker's input from being squeezed thin
  // enough that the Total column (its immediate neighbour) starts to overlap
  // it, the other half of the same overlap bug.
  dateCell: {
    minWidth: '140px',
    // Fluent's DatePicker keeps an intrinsic ~168px input that will not shrink to a narrower cell;
    // this lets it fill (not overflow) the cell it is in. Measured in a real browser.
    '& .fui-Input': { minWidth: '0', width: '100%' },
    '& input': { minWidth: '0' },
  },

  /**
   * Wireframe S1b note 3 — the grand total is NOT a table row: it belongs to
   * the whole list, so it is a distinct surface pinned below it in BOTH faces
   * (`useSurfaceStyles().inset`) rather than a `<tfoot>` the mobile cards
   * would lose. Same treatment the reviewer's own read-only view uses.
   */
  grandTotalRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_X.m,
  },

  /**
   * The undo toast's body, on `useSurfaceStyles().inset`. `<output>` is a
   * native form element, and those do NOT inherit `color` from
   * `FluentProvider` — they fall back to the browser's `buttontext` system
   * colour, which collides with pure black in high contrast (log entry 54).
   */
  undoBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    color: tokens.colorNeutralForeground1,
  },

  /**
   * Wireframe R2b — the error summary is its own block, distinct from the
   * per-field messages it links to. The red banner itself is a Fluent
   * `MessageBar intent="error"` (palette colours are only ever allowed inside
   * one), and the linked issues sit BESIDE it rather than inside its body:
   * a `<Button>` inside `MessageBarBody` sends Fluent into an infinite render
   * loop (CLAUDE.md log entry 9), and every issue here is a button.
   *
   * No `outline` anywhere, deliberately — `src/test/keyboardContract.test.ts`
   * greps the whole tree for one, and Fluent's own focus ring is what marks
   * this element when the submit handler focuses it.
   */
  summaryCard: { display: 'flex', flexDirection: 'column', gap: SPACE.m },
  errorSummaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACE.xs,
    margin: SPACE.none,
    paddingLeft: SPACE_X.l,
  },

  /**
   * Wireframe R6 — a full-screen milestone, the same shape the reviewer's own
   * "Purchase Requisition created" screen uses. Measured rather than fluid: it
   * is two sentences of copy, and a sentence set across the whole tab canvas
   * is unreadable.
   */
  milestone: { alignItems: 'center', textAlign: 'center' },

  historyEntry: { display: 'flex', flexDirection: 'column', gap: SPACE.xs },
  historySectionBadge: { marginLeft: SPACE_X.s },
  rejectionSectionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACE_X.s,
    marginTop: SPACE.s,
  },
});

/**
 * Wireframe R8 — a "changed from" hint under a field whose value no longer
 * matches what it was when this request was rejected. `original` is `null`
 * outside that flow (a fresh draft, or the request loaded but was never
 * rejected), in which case nothing has anything to be "changed from".
 */
function changedFromHint(
  original: Record<string, string> | null,
  key: string,
  current: string,
): string | undefined {
  if (!original) return undefined;
  const before = original[key];
  if (before === undefined || before === current) return undefined;
  return `Changed from “${before || '—'}”`;
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  CREATED: 'Draft created',
  SUBMITTED: 'Submitted for review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RESUBMITTED: 'Resubmitted for review',
  REVIEWER_EDITED: 'Edited by a reviewer',
  PR_CREATED: 'Purchase Requisition created in D365',
  // #132 — the requisition exists, its files did not make it. Worded for the
  // requester, whose own quotes these are, rather than left as a raw enum.
  PR_ATTACHMENTS_FAILED: 'Attachments could not be linked in D365',
  // #235 — a Teams/email notification the app sends automatically (PR
  // created, or this rejection) failed to deliver. The request itself is
  // unaffected; the comment on the row names which notification and why.
  NOTIFICATION_FAILED: 'Notification failed to send',
  DELETED: 'Deleted',
};

function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

/** #148 — unambiguous date+time (`formatDateTime` from the shared formatter), not the
 * browser's default locale, which on `en-US` renders the ambiguous `MM/DD/YYYY`. */
function formatHistoryDate(iso: string): string {
  return formatDateTime(iso);
}

/**
 * #144 — once a request leaves DRAFT/REJECTED it is read-only (sheet R9):
 * the review decision, or D365 itself, is now the only thing that can change
 * it, and re-showing the editable form let a requester silently alter or
 * resubmit something already decided.
 */
const READONLY_STATUS_MESSAGES: Partial<Record<RequestStatus, string>> = {
  PENDING_REVIEW: 'This request is with the review group and can no longer be edited.',
  APPROVED: 'This request has been approved and can no longer be edited.',
  PR_CREATED:
    'This request has been approved and its Purchase Requisition created in D365. It can no longer be edited.',
  // DRAFT and REJECTED are normally editable — this only applies once #222's
  // ownership check has ruled the viewer out (a reviewer looking at someone
  // else's rejected request via the role-aware Dashboard's "View" action).
  DRAFT: 'This request belongs to someone else and can only be viewed here.',
  REJECTED: 'This request belongs to someone else and can only be viewed here.',
};

/**
 * Manages one dependent list's full lifecycle against the selected company:
 * disabled with no company, loading on company change, ready or error after.
 * A separate instance per field is what gives each its own loading/error
 * state independently of the other two (wireframe R2c).
 */
function useDependentField<T>(company: string | null, fetcher: (company: string) => Promise<T[]>) {
  const [state, setState] = useState<Rows<T>>({ phase: 'disabled' });
  // The company the `ready` rows were fetched FOR. Right after a company change
  // `state` still holds the OLD company's rows for one render; anything that
  // acts on the rows (defaulting a selection) must check this first.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const load = useCallback(
    async (forCompany: string, signal?: AbortSignal) => {
      setState({ phase: 'loading' });
      try {
        const rows = await fetcher(forCompany);
        if (signal?.aborted) return;
        setState({ phase: 'ready', rows });
        setLoadedFor(forCompany);
      } catch {
        if (signal?.aborted) return;
        setState({ phase: 'error' });
      }
    },
    [fetcher],
  );

  useEffect(() => {
    if (!company) {
      setState({ phase: 'disabled' });
      setLoadedFor(null);
      return undefined;
    }
    const controller = new AbortController();
    void load(company, controller.signal);
    return () => controller.abort();
  }, [company, load]);

  const retry = useCallback(() => {
    if (company) void load(company);
  }, [company, load]);

  return { state, retry, loadedFor };
}

/**
 * Same lifecycle as `useDependentField` (disabled/loading/ready/error), gated
 * on a plain boolean rather than a company code — for a list that is only
 * ever meaningful once a condition elsewhere on the form holds (projects, per
 * masterdata.ts: "only needed once the purchase type is Capital"). A fetcher
 * whose identity changes (e.g. one closing over the company) re-fetches.
 */
function useEnabledOnceFetched<T>(enabled: boolean, fetcher: () => Promise<T[]>) {
  const [state, setState] = useState<Rows<T>>({ phase: 'disabled' });

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setState({ phase: 'loading' });
      try {
        const rows = await fetcher();
        if (signal?.aborted) return;
        setState({ phase: 'ready', rows });
      } catch {
        if (signal?.aborted) return;
        setState({ phase: 'error' });
      }
    },
    [fetcher],
  );

  useEffect(() => {
    if (!enabled) {
      setState({ phase: 'disabled' });
      return undefined;
    }
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [enabled, load]);

  const retry = useCallback(() => {
    if (enabled) void load();
  }, [enabled, load]);

  return { state, retry };
}

interface DependentComboboxProps<T> {
  id: string;
  label: string;
  company: string | null;
  field: { state: Rows<T>; retry: () => void };
  getKey: (row: T) => string;
  getLabel: (row: T) => string;
  selectedValue: string | null;
  onSelect: (value: string | undefined) => void;
  /** Literal message from `requestHeaderSchema` (#32) — shown only once the field is ready. */
  errorMessage?: string;
  onBlur: () => void;
  /**
   * Whether the label carries the required marker. Defaults to true; cost centre and
   * line of business pass `false` — a requester may leave them for the reviewer.
   */
  required?: boolean;
  /**
   * Shows Fluent's clear (✕) button while a value is selected. Typing the text away does not
   * deselect (on blur the field snaps back), so without this an optional field that was
   * pre-selected by default could never be emptied. Only for optional fields.
   */
  clearable?: boolean;
}

/** One dependent dropdown, in whichever of the four R2c/S3a states it currently holds. */
function DependentCombobox<T>({
  id,
  label,
  company,
  field,
  getKey,
  getLabel,
  selectedValue,
  onSelect,
  errorMessage,
  onBlur,
  required = true,
  clearable = false,
}: DependentComboboxProps<T>) {
  const styles = useStyles();
  const { state, retry } = field;
  useAutoRetry(state.phase, retry);

  const placeholder = !company
    ? 'Select a company first'
    : state.phase === 'loading'
      ? 'Loading…'
      : state.phase === 'error'
        ? 'Unavailable — D365 not reachable'
        : `Select a ${label.toLowerCase()}`;

  const selectedRow =
    selectedValue && state.phase === 'ready'
      ? state.rows.find((row) => getKey(row) === selectedValue)
      : undefined;
  const displayValue = selectedRow ? getLabel(selectedRow) : (selectedValue ?? '');
  // A validation message while the field is disabled/loading/unreachable would
  // read as nonsense ("required" on a control the user cannot yet interact
  // with) — only show it once the field is actually usable.
  const showValidation = state.phase === 'ready' && !!errorMessage;

  return (
    <Field
      label={label}
      required={required}
      validationState={showValidation ? 'error' : 'none'}
      validationMessage={showValidation ? errorMessage : undefined}
    >
      <FilterableCombobox
        id={id}
        placeholder={placeholder}
        disabled={state.phase !== 'ready'}
        clearable={clearable}
        options={
          state.phase === 'ready'
            ? state.rows.map((row) => ({ value: getKey(row), label: getLabel(row) }))
            : []
        }
        selectedValue={selectedValue}
        fallbackLabel={displayValue}
        onSelect={(value) => onSelect(value ?? undefined)}
        onBlur={onBlur}
      />
      {state.phase === 'loading' && (
        <div className={styles.hint}>
          <Spinner size="tiny" label={`Loading ${label.toLowerCase()}…`} labelPosition="after" />
        </div>
      )}
      {state.phase === 'error' && (
        <Body1 as="p" className={styles.hint}>
          Unavailable — D365 not reachable. Retrying automatically.{' '}
          <Button appearance="transparent" size="small" onClick={retry}>
            Retry now
          </Button>
        </Body1>
      )}
      {state.phase === 'ready' && state.rows.length === 0 && (
        <Body1 as="p" className={styles.hint}>
          {noD365MatchHint(label)}
        </Body1>
      )}
    </Field>
  );
}

/**
 * Wireframe E2 — "no match" was previously implemented for the vendor search
 * only; every other D365-backed combobox rendered an empty listbox with no
 * explanation when D365 genuinely has nothing configured (#160). Same wording
 * shape as the vendor field's own message, minus the search term since these
 * fields aren't searched — they list everything D365 returns for the current
 * scope.
 */
function noD365MatchHint(label: string): string {
  return `No ${label.toLowerCase()} found in D365. Ask your D365 administrator to set one up, then try again.`;
}

type VendorSearch =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'ready'; rows: VendorRow[] }
  | { phase: 'error' };

function vendorLabel(row: VendorRow): string {
  return `${row.accountNumber} — ${row.name}`;
}

function vendorDetail(row: VendorRow): string {
  // Any of these can come back empty — D365 does not populate a city for every
  // vendor, and the API sends '' rather than omitting the field.
  return [row.paymentTerms, row.currencyCode, row.city]
    .filter((part) => part.length > 0)
    .join(' · ');
}

/**
 * Debounced server-side vendor search (wireframe R2d). The vendor master is
 * the largest list in the app, so nothing is fetched below
 * `VENDOR_SEARCH_MIN_CHARS` — the API enforces the same minimum independently.
 *
 * `selectedLabel` is the formatted "accountNumber — name" text a selection
 * just wrote into the field (see `handleVendorSelect`). Selecting an option
 * changes `query` — the very state this hook searches on — so without this
 * guard, every selection immediately re-triggers a search for its own
 * composed label, which matches no vendor, and silently overwrites the just
 * -made selection with a false "no vendors match" state. Once the field is
 * edited again, `onChange` clears the selection and `selectedLabel` goes
 * back to null, so normal search resumes.
 *
 * Read via a ref, not a dependency: `selectedLabel` changes in the same tick
 * as `query` (both set inside `handleVendorSelect`), and `debounced` only
 * catches up 300ms later. Putting it in the fetch effect's dependency array
 * would re-run that effect immediately, on the *old* `debounced` value, one
 * redundant search before the debounce timer even fires.
 */
function useVendorSearch(query: string, selectedLabel: string | null, company: string | null) {
  const [debounced, setDebounced] = useState('');
  const [state, setState] = useState<VendorSearch>({ phase: 'idle' });
  const selectedLabelRef = useRef(selectedLabel);
  selectedLabelRef.current = selectedLabel;

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), VENDOR_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // `company` is a dependency on purpose: vendors are per-company in F&O, so
  // changing the company re-runs a pending search against the new one.
  const load = useCallback(
    async (term: string, signal?: AbortSignal) => {
      setState({ phase: 'loading' });
      try {
        const rows = await getVendors(term, 1, company);
        if (signal?.aborted) return;
        setState({ phase: 'ready', rows });
      } catch {
        if (signal?.aborted) return;
        setState({ phase: 'error' });
      }
    },
    [company],
  );

  useEffect(() => {
    // No company, no search: without one D365 answers from the service
    // account's default company, which is not the requester's — an empty or
    // wrong list that reads as "this vendor does not exist".
    if (
      !company ||
      debounced.length < VENDOR_SEARCH_MIN_CHARS ||
      debounced === selectedLabelRef.current
    ) {
      return undefined;
    }
    const controller = new AbortController();
    void load(debounced, controller.signal);
    return () => controller.abort();
  }, [company, debounced, load]);

  useEffect(() => {
    if (!company || debounced.length < VENDOR_SEARCH_MIN_CHARS) setState({ phase: 'idle' });
  }, [company, debounced]);

  const retry = useCallback(() => {
    if (company && debounced.length >= VENDOR_SEARCH_MIN_CHARS) void load(debounced);
  }, [company, debounced, load]);

  return { state, debounced, retry };
}

type OnceFetched<T> = { phase: 'loading' } | { phase: 'ready'; rows: T[] } | { phase: 'error' };

/**
 * Fetches once per form load and never again per row — the behaviour #26
 * requires for procurement categories and units, which every line shares.
 */
function useOnceFetched<T>(fetcher: () => Promise<T[]>) {
  const [state, setState] = useState<OnceFetched<T>>({ phase: 'loading' });

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setState({ phase: 'loading' });
      try {
        const rows = await fetcher();
        if (signal?.aborted) return;
        setState({ phase: 'ready', rows });
      } catch {
        if (signal?.aborted) return;
        setState({ phase: 'error' });
      }
    },
    [fetcher],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => void load(), [load]);
  return { state, retry };
}

function projectKey(row: ProjectRow): string {
  return row.ProjectID;
}

function projectLabel(row: ProjectRow): string {
  return row.ProjectName.length > 0 ? `${row.ProjectID} — ${row.ProjectName}` : row.ProjectID;
}

/** SOW §4.3 — the 8 fields per line. The two vendor-facing ones are behind the
 * row expander so the table never scrolls horizontally, even at 20 lines. */
interface LineItemDraft {
  id: string;
  itemDescription: string;
  procurementCategoryId: string | null;
  quantity: string;
  unitOfMeasure: string | null;
  netPrice: string;
  deliveryDate: Date | null;
  /** Optional period the line covers. */
  startDate: Date | null;
  endDate: Date | null;
  /** Optional D365 sales tax group code (company-scoped, from the live list). */
  salesTaxGroupId: string | null;
  // Not editable on the form any more (the column was hidden at the client's request) but kept
  // so a request that already carries them is not silently emptied by an edit.
  vendorItemDescription: string;
  vendorPartNo: string;
}

let nextLineSeq = 0;
/**
 * `seedDeliveryDate` defaults a fresh line to the header's own delivery date
 * (R2/R3 note 1) — most lines ship on the same date as the header, and
 * leaving it blank meant every added line needed the same date picked again
 * by hand. Passing nothing (a line hydrated from a loaded request, or the
 * one line "+ Add first line" creates before the header's delivery date has
 * been set) keeps the old blank behaviour.
 */
function newLine(seedDeliveryDate: Date | null = null): LineItemDraft {
  nextLineSeq += 1;
  return {
    id: `line-${nextLineSeq}`,
    itemDescription: '',
    procurementCategoryId: null,
    quantity: '',
    unitOfMeasure: null,
    netPrice: '',
    deliveryDate: seedDeliveryDate,
    startDate: null,
    endDate: null,
    salesTaxGroupId: null,
    vendorItemDescription: '',
    vendorPartNo: '',
  };
}

/**
 * paymentTerms/currencyCode have no control of their own — they're read-only,
 * auto-filled the moment a vendor is picked (R2d note 3). When NO vendor is
 * picked yet, their Zod issues would just repeat "pick a vendor" with
 * confusing generic wording, so they're hidden in that case — vendorAccount's
 * own message already covers the gap.
 *
 * But a vendor CAN be picked and still leave one of these blank: D365's
 * vendor master does not guarantee either is populated (confirmed live —
 * some vendors carry an empty payment-terms string). In that case the issue
 * is real and has nothing to do with picking a vendor, so it must NOT be
 * hidden — see `issues` below, which only hides these paths when a
 * `vendorAccount` issue is *also* present.
 */
const HIDDEN_ISSUE_PATHS = new Set(['paymentTerms', 'currencyCode']);

type LineItemField =
  | 'itemDescription'
  | 'procurementCategoryId'
  | 'quantity'
  | 'unitOfMeasure'
  | 'netPrice'
  | 'deliveryDate'
  | 'startDate'
  | 'endDate'
  | 'salesTaxGroupId';

const EMPTY_TOUCHED: ReadonlySet<LineItemField> = new Set();

/** Decimal-safe (never a JS float, per CLAUDE.md) — null while quantity/unit price aren't valid yet.
 * Thin wrapper over the shared `computeLineTotal` (`lib/totals.ts`), which `ReviewerQueue.tsx`'s
 * read-only review-detail screen also uses, so the two screens can never compute different totals
 * for the same line (#147). */
function lineTotal(line: LineItemDraft): Decimal | null {
  return computeLineTotal(line.quantity, line.netPrice);
}

/** `z.iso.date()` wants a date-only string — never the full ISO instant. */
function toIsoDate(date: Date | null): string | undefined {
  // The calendar day the picker shows — NOT `toISOString()`, which moves it in every zone but UTC.
  return date ? toDateOnly(date) : undefined;
}

/**
 * Entry-time floor for every delivery-date picker (#231) — a requester should
 * not be able to pick an already-past date. This is defense in depth only: it
 * does not solve the actual bug (a date valid at submission going stale while
 * a request sits in the review queue), which `attemptPrCreation` on the API
 * side guards at approval time instead. Local midnight, not UTC, since this
 * only constrains what a human can click in their own calendar.
 */
function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/** Wireframe R7's own example is "Draft saved 14:32" — 24-hour, no seconds (#148, shared `formatTime`). */
function formatSavedAt(date: Date): string {
  return formatTime(date);
}

/** Wireframe R5's own examples read "01 Aug 2026" — matches the review screen, not an ISO
 * date (#148, shared `formatDate` — this wrapper only keeps the null-safe convenience the
 * many call sites below rely on). */
function formatReviewDate(date: Date | null): string {
  return date ? formatDate(date) : '';
}

/**
 * The draft schema accepts every line field as optional (#28), so a field
 * that isn't yet a valid value is simply omitted rather than sent invalid —
 * auto-save must never fail just because a line is mid-edit.
 */
function lineToDraftPayload(line: LineItemDraft): NonNullable<DraftRequestInput['lines']>[number] {
  return {
    itemDescription: line.itemDescription.trim() || undefined,
    // `||`, not `??`: an empty string is "not chosen yet", and sending it fails
    // the draft schema's min(1) — which blocked every auto-save.
    procurementCategoryId: line.procurementCategoryId || undefined,
    quantity:
      QUANTITY_PATTERN.test(line.quantity) && Number(line.quantity) > 0 ? line.quantity : undefined,
    unitOfMeasure: line.unitOfMeasure ?? undefined,
    netPrice: NET_PRICE_PATTERN.test(line.netPrice) ? line.netPrice : undefined,
    deliveryDate: toIsoDate(line.deliveryDate),
    startDate: toIsoDate(line.startDate),
    endDate: toIsoDate(line.endDate),
    salesTaxGroupId: line.salesTaxGroupId || undefined,
    vendorItemDescription: line.vendorItemDescription.trim() || undefined,
    vendorPartNo: line.vendorPartNo.trim() || undefined,
  };
}

/** The category's display label, resolved from the shared list — falls back to the raw id until it loads. */
function categoryDisplayLabel(
  categories: OnceFetched<ProcurementCategoryRow>,
  id: string | null,
): string {
  return resolveCategoryLabel(categories, id);
}

interface CategoryComboboxProps {
  id: string;
  className?: string;
  listboxClassName?: string;
  'aria-label'?: string;
  invalid: boolean;
  categories: OnceFetched<ProcurementCategoryRow>;
  selectedId: string | null;
  onSelect: (categoryId: string | null) => void;
  onBlur: () => void;
}

/**
 * The procurement-category picker, shared by the desktop line table and the
 * stacked mobile card. Typing filters the list (substring, case-insensitive,
 * on the visible label) — the earlier version bound `value` to the selected
 * label with no `onChange`, so every keystroke was thrown away. While the user
 * types, the input shows what they typed; picking an option or leaving the
 * field returns it to the selected label.
 */
function CategoryCombobox({
  id,
  className,
  listboxClassName,
  'aria-label': ariaLabel,
  invalid,
  categories,
  selectedId,
  onSelect,
  onBlur,
}: CategoryComboboxProps) {
  const rows = categories.phase === 'ready' ? categories.rows : [];
  const selectedRow = rows.find((row) => categoryMatches(row, selectedId));

  return (
    <FilterableCombobox
      id={id}
      className={className}
      listbox={listboxClassName ? { className: listboxClassName } : undefined}
      aria-label={ariaLabel}
      aria-invalid={invalid ? true : undefined}
      placeholder={categories.phase === 'loading' ? 'Loading…' : 'Select'}
      disabled={categories.phase !== 'ready'}
      clearable
      options={rows.map((row) => ({ value: categoryKey(row), label: categoryLabel(row) }))}
      selectedValue={selectedRow ? categoryKey(selectedRow) : null}
      fallbackLabel={categoryDisplayLabel(categories, selectedId)}
      onSelect={onSelect}
      onBlur={onBlur}
    />
  );
}

/** The unit list as dropdown options — `EA — Each`. */
function unitOptions(units: OnceFetched<UnitRow>): { value: string; label: string }[] {
  return units.phase === 'ready'
    ? units.rows.map((row) => ({
        value: row.UnitSymbol,
        label: `${row.UnitSymbol} — ${row.UnitDescription}`,
      }))
    : [];
}

/** The unit's display label, on the same terms as `categoryDisplayLabel`. */
function unitDisplayLabel(units: OnceFetched<UnitRow>, symbol: string | null): string {
  if (!symbol) return '';
  if (units.phase !== 'ready') return symbol;
  const row = units.rows.find((candidate) => candidate.UnitSymbol === symbol);
  return row ? `${row.UnitSymbol} — ${row.UnitDescription}` : symbol;
}

/**
 * The optional Sales tax dropdown. Its options are the company's own sales tax groups, read live
 * from D365 — never a list kept here. It can be empty or unavailable (no company yet, still
 * loading, D365 unreachable, nothing set up), and in every one of those cases it is simply
 * disabled with a plain placeholder: the field is optional, so it must never stand in the way.
 */
function SalesTaxCombobox({
  id,
  className,
  salesTaxGroups,
  selectedId,
  onSelect,
  onBlur,
  ...aria
}: {
  id: string;
  className?: string;
  salesTaxGroups: Rows<SalesTaxGroupRow>;
  selectedId: string | null;
  onSelect: (code: string | null) => void;
  onBlur: () => void;
  'aria-label'?: string;
}) {
  const styles = useStyles();
  const options = salesTaxGroups.phase === 'ready' ? salesTaxGroups.rows : [];
  const placeholder =
    salesTaxGroups.phase === 'disabled'
      ? 'Pick a company first'
      : salesTaxGroups.phase === 'loading'
        ? 'Loading…'
        : salesTaxGroups.phase === 'error'
          ? 'Could not load'
          : options.length === 0
            ? 'None set up'
            : 'Select';
  return (
    <FilterableCombobox
      id={id}
      className={className}
      listbox={{ className: styles.wideListboxPopup }}
      {...aria}
      clearable
      placeholder={placeholder}
      // Locked only when there is nothing to pick AND nothing to remove: a saved choice must stay
      // clearable even when D365's list is unavailable, or the requester is stuck with it.
      disabled={!selectedId && (salesTaxGroups.phase !== 'ready' || options.length === 0)}
      options={options.map((row) => ({
        value: row.code,
        label: codeLabel(row.code, row.description),
      }))}
      selectedValue={selectedId}
      fallbackLabel={salesTaxLabel(salesTaxGroups, selectedId)}
      onSelect={onSelect}
      onBlur={onBlur}
    />
  );
}

interface LineItemRowProps {
  line: LineItemDraft;
  index: number;
  lineCount: number;
  categories: OnceFetched<ProcurementCategoryRow>;
  units: OnceFetched<UnitRow>;
  salesTaxGroups: Rows<SalesTaxGroupRow>;
  /** #148 — the vendor's currency, for grouping the unit price / line total the same way
   * the review screen and reviewer queue do. Empty string until a vendor is picked. */
  currencyCode: string;
  touched: ReadonlySet<string>;
  /** Literal messages from `requestLineSchema` (#32) — never hand-written text. */
  errors: Partial<Record<LineItemField, string>>;
  /** True once a submit attempt has been made — shows every message, not just touched ones. */
  forceShowErrors: boolean;
  onChange: (patch: Partial<LineItemDraft>) => void;
  onTouch: (field: LineItemField) => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

/**
 * One line, inline-editable, in whichever validation state its fields
 * currently hold. Renders as up to three real `<tr>`s: the fields, an
 * optional consolidated error message, and an optional expanded vendor-field
 * panel — so nothing ever shifts a column's width to make room for a message.
 *
 * Each field still gets its own red border via `aria-invalid` (Fluent's
 * Input/Combobox/DatePicker all key their invalid styling off that prop
 * directly, no `Field` wrapper required) — a per-cell `Field` would work too,
 * but re-introduces the column-width-jump problem #30 built the row-level
 * message specifically to avoid.
 */
function LineItemRow({
  line,
  index,
  lineCount,
  categories,
  units,
  salesTaxGroups,
  currencyCode,
  touched,
  errors,
  forceShowErrors,
  onChange,
  onTouch,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onRemove,
}: LineItemRowProps) {
  const styles = useStyles();
  const visibleErrors = (Object.entries(errors) as [LineItemField, string][]).filter(
    ([field]) => touched.has(field) || forceShowErrors,
  );
  const total = lineTotal(line);
  const fieldId = (field: LineItemField) => `field-lines-${String(index)}-${field}`;

  const unitValue = unitDisplayLabel(units, line.unitOfMeasure);

  /**
   * Wireframe R3a note 3 — Escape cancels an inline desktop row edit. Every
   * text field here is bound straight to `line` state (there is no separate
   * "draft" value to discard), so "cancel" means reverting to whatever the
   * field held when it was last focused and moving focus away — the same
   * shape as a spreadsheet cell edit. The snapshot lives in a ref, not
   * state: capturing it is a side effect of focusing, not something that
   * should itself trigger a render.
   */
  type TextField = 'itemDescription' | 'quantity' | 'netPrice';
  const editSnapshot = useRef<Partial<Record<TextField, string>>>({});

  function captureSnapshot(field: TextField, value: string) {
    return () => {
      editSnapshot.current[field] = value;
    };
  }

  function cancelOnEscape(field: TextField) {
    return (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      const snapshot = editSnapshot.current[field];
      if (snapshot !== undefined) onChange({ [field]: snapshot });
      event.currentTarget.blur();
    };
  }

  return (
    <>
      <TableRow>
        <TableCell className={styles.alignCenter}>
          <Input
            id={fieldId('itemDescription')}
            className={styles.cellInput}
            aria-label={`Item description, line ${String(index + 1)}`}
            aria-invalid={errors.itemDescription ? true : undefined}
            value={line.itemDescription}
            onChange={(_, data) => onChange({ itemDescription: data.value })}
            onFocus={captureSnapshot('itemDescription', line.itemDescription)}
            onKeyDown={cancelOnEscape('itemDescription')}
            onBlur={() => onTouch('itemDescription')}
          />
        </TableCell>
        <TableCell className={styles.alignCenter}>
          <CategoryCombobox
            id={fieldId('procurementCategoryId')}
            className={styles.cellInput}
            listboxClassName={styles.wideListboxPopup}
            aria-label={`Procurement category, line ${String(index + 1)}`}
            invalid={Boolean(errors.procurementCategoryId)}
            categories={categories}
            selectedId={line.procurementCategoryId}
            onSelect={(procurementCategoryId) => onChange({ procurementCategoryId })}
            onBlur={() => onTouch('procurementCategoryId')}
          />
        </TableCell>
        <TableCell className={styles.alignCenter}>
          <Input
            id={fieldId('quantity')}
            className={mergeClasses(styles.cellInput, styles.numericCell)}
            aria-label={`Quantity, line ${String(index + 1)}`}
            aria-invalid={errors.quantity ? true : undefined}
            value={line.quantity}
            onChange={(_, data) => onChange({ quantity: data.value })}
            onFocus={captureSnapshot('quantity', line.quantity)}
            onKeyDown={cancelOnEscape('quantity')}
            onBlur={() => onTouch('quantity')}
          />
        </TableCell>
        <TableCell className={styles.alignCenter}>
          <FilterableCombobox
            id={fieldId('unitOfMeasure')}
            className={styles.cellInput}
            listbox={{ className: styles.wideListboxPopup }}
            aria-label={`Unit of measure, line ${String(index + 1)}`}
            aria-invalid={errors.unitOfMeasure ? true : undefined}
            placeholder={units.phase === 'loading' ? 'Loading…' : 'Select'}
            disabled={units.phase !== 'ready'}
            options={unitOptions(units)}
            selectedValue={line.unitOfMeasure}
            fallbackLabel={unitValue}
            onSelect={(unitOfMeasure) => onChange({ unitOfMeasure })}
            onBlur={() => onTouch('unitOfMeasure')}
          />
        </TableCell>
        <TableCell className={styles.alignCenter}>
          <Input
            id={fieldId('netPrice')}
            className={mergeClasses(styles.cellInput, styles.numericCell)}
            aria-label={`Unit price, line ${String(index + 1)}`}
            aria-invalid={errors.netPrice ? true : undefined}
            value={line.netPrice}
            onChange={(_, data) => onChange({ netPrice: data.value })}
            onFocus={captureSnapshot('netPrice', line.netPrice)}
            onKeyDown={cancelOnEscape('netPrice')}
            onBlur={() => onTouch('netPrice')}
          />
        </TableCell>
        <TableCell className={mergeClasses(styles.dateCell, styles.alignCenter)}>
          <DatePicker
            id={fieldId('deliveryDate')}
            aria-label={`Delivery date, line ${String(index + 1)}`}
            aria-invalid={errors.deliveryDate ? true : undefined}
            value={line.deliveryDate}
            minDate={startOfToday()}
            onSelectDate={(date) => {
              onChange({ deliveryDate: date ?? null });
              onTouch('deliveryDate');
            }}
          />
        </TableCell>
        <TableCell className={mergeClasses(styles.dateCell, styles.alignCenter)}>
          <DatePicker
            id={fieldId('startDate')}
            aria-label={`Start date, line ${String(index + 1)}`}
            aria-invalid={errors.startDate ? true : undefined}
            value={line.startDate}
            onSelectDate={(date) => {
              onChange({ startDate: date ?? null });
              onTouch('startDate');
            }}
          />
        </TableCell>
        <TableCell className={mergeClasses(styles.dateCell, styles.alignCenter)}>
          <DatePicker
            id={fieldId('endDate')}
            aria-label={`End date, line ${String(index + 1)}`}
            aria-invalid={errors.endDate ? true : undefined}
            value={line.endDate}
            onSelectDate={(date) => {
              onChange({ endDate: date ?? null });
              onTouch('endDate');
            }}
          />
        </TableCell>
        <TableCell className={styles.alignCenter}>
          <SalesTaxCombobox
            id={fieldId('salesTaxGroupId')}
            className={styles.cellInput}
            aria-label={`Sales tax, line ${String(index + 1)}`}
            salesTaxGroups={salesTaxGroups}
            selectedId={line.salesTaxGroupId}
            onSelect={(salesTaxGroupId) => onChange({ salesTaxGroupId })}
            onBlur={() => onTouch('salesTaxGroupId')}
          />
        </TableCell>
        <TableCell className={mergeClasses(styles.stickyTotal, styles.alignCenter)}>
          <TableCellLayout className={styles.lineTotalCell}>
            <Body1Strong>{total ? formatTotal(total, currencyCode) : '—'}</Body1Strong>
          </TableCellLayout>
        </TableCell>
        <TableCell className={mergeClasses(styles.stickyActions, styles.alignCenter)}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="transparent" size="small">
                Actions
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={onDuplicate}>Duplicate</MenuItem>
                <MenuItem onClick={onMoveUp} disabled={index === 0}>
                  Move up
                </MenuItem>
                <MenuItem onClick={onMoveDown} disabled={index === lineCount - 1}>
                  Move down
                </MenuItem>
                <MenuItem onClick={onRemove}>Remove</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </TableCell>
      </TableRow>
      {visibleErrors.length > 0 && (
        <TableRow>
          <TableCell colSpan={11}>
            <Body1 as="p" className={styles.errorText}>
              {visibleErrors.map(([, message]) => message).join(' ')}
            </Body1>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * The same line, below `MOBILE_BREAKPOINT_PX` (#53, wireframe S1b).
 *
 * A card rather than a row, with every one of the eight fields labelled and
 * present — including the two vendor fields the table keeps behind an expander,
 * because there is no width to save here and hiding them would mean the mobile
 * view showed less than the desktop one. The labels are the table's own column
 * headings verbatim, so both presentations describe the same data in the same
 * words. Editing is not inline: it opens the full-screen sheet below, which is
 * the only way eight controls fit a 390px canvas without a sideways scroll.
 */
function LineItemCard({
  line,
  index,
  lineCount,
  categories,
  units,
  salesTaxGroups,
  currencyCode,
  touched,
  errors,
  forceShowErrors,
  onEdit,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  line: LineItemDraft;
  index: number;
  lineCount: number;
  categories: OnceFetched<ProcurementCategoryRow>;
  units: OnceFetched<UnitRow>;
  salesTaxGroups: Rows<SalesTaxGroupRow>;
  currencyCode: string;
  touched: ReadonlySet<string>;
  errors: Partial<Record<LineItemField, string>>;
  forceShowErrors: boolean;
  onEdit: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  const styles = useStyles();
  const visibleErrors = (Object.entries(errors) as [LineItemField, string][]).filter(
    ([field]) => touched.has(field) || forceShowErrors,
  );
  const total = lineTotal(line);
  const lineNumber = String(index + 1);

  return (
    <DataCard
      heading={`Line ${lineNumber}`}
      fields={[
        { label: 'Item description', value: line.itemDescription || '—' },
        {
          label: 'Procurement category',
          value: categoryDisplayLabel(categories, line.procurementCategoryId) || '—',
        },
        { label: 'Quantity', value: line.quantity || '—' },
        { label: 'Unit', value: unitDisplayLabel(units, line.unitOfMeasure) || '—' },
        {
          label: 'Unit price',
          value: line.netPrice ? formatNetPrice(line.netPrice, currencyCode) : '—',
        },
        { label: 'Delivery date', value: formatReviewDate(line.deliveryDate) || '—' },
        { label: 'Start date', value: formatReviewDate(line.startDate) || '—' },
        { label: 'End date', value: formatReviewDate(line.endDate) || '—' },
        {
          label: 'Sales tax',
          value: salesTaxLabel(salesTaxGroups, line.salesTaxGroupId) || '—',
        },
        { label: 'Total', value: total ? formatTotal(total, currencyCode) : '—' },
        ...(visibleErrors.length > 0
          ? [
              {
                label: 'Needs fixing',
                value: (
                  <span className={styles.errorText}>
                    {visibleErrors.map(([, message]) => message).join(' ')}
                  </span>
                ),
              },
            ]
          : []),
      ]}
      actions={
        <>
          {/* `secondary`, not `primary`: exactly one filled button may be
              visible per screen, and it belongs to the wizard footer's own
              next step — not repeated once per line card (#202). */}
          <Button appearance="secondary" onClick={onEdit}>
            Edit line {lineNumber}
          </Button>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="transparent" aria-label={`Actions for line ${lineNumber}`}>
                Actions
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={onDuplicate}>Duplicate</MenuItem>
                <MenuItem onClick={onMoveUp} disabled={index === 0}>
                  Move up
                </MenuItem>
                <MenuItem onClick={onMoveDown} disabled={index === lineCount - 1}>
                  Move down
                </MenuItem>
                <MenuItem onClick={onRemove}>Remove</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </>
      }
    />
  );
}

/**
 * The mobile line editor (#53: "line editing on mobile opens a full-screen
 * sheet").
 *
 * Every field the table row edits, one per line in a single column, wired to
 * exactly the same `onChange`/`onTouch` handlers — the sheet is a different
 * presentation of the same editing surface, never a second implementation of
 * it. Fluent v9 has no full-screen `DialogSurface` variant, so the surface is
 * sized to the viewport by `useResponsiveStyles().mobileSheet`.
 */
function LineItemSheet({
  line,
  index,
  categories,
  units,
  salesTaxGroups,
  currencyCode,
  errors,
  onChange,
  onTouch,
  onClose,
}: {
  line: LineItemDraft;
  index: number;
  categories: OnceFetched<ProcurementCategoryRow>;
  units: OnceFetched<UnitRow>;
  salesTaxGroups: Rows<SalesTaxGroupRow>;
  currencyCode: string;
  errors: Partial<Record<LineItemField, string>>;
  onChange: (patch: Partial<LineItemDraft>) => void;
  onTouch: (field: LineItemField) => void;
  onClose: () => void;
}) {
  const styles = useStyles();
  const responsive = useResponsiveStyles();
  const lineNumber = String(index + 1);
  const fieldId = (field: LineItemField) => `field-lines-${String(index)}-${field}`;
  const total = lineTotal(line);

  // Sheet 10's shortcut row: "Enter — commit a row edit. Never submits the
  // request." Only on the plain text inputs: inside the comboboxes Enter picks
  // the highlighted option and inside the DatePicker it commits the date, and
  // stealing it there would break the control rather than help. Nothing is
  // "saved" by committing — every keystroke is already applied to the line —
  // so the commit is simply closing the sheet.
  const commitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onClose();
    }
  };

  // K1 "Add line → the new row's Item description, already in edit": below the
  // breakpoint the sheet *is* the edit surface, so opening it must land here.
  const itemDescriptionRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    itemDescriptionRef.current?.focus();
  }, []);

  function validation(field: LineItemField) {
    return {
      validationState: errors[field] ? ('error' as const) : ('none' as const),
      validationMessage: errors[field],
    };
  }

  return (
    <Dialog open onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={mergeClasses(responsive.mobileSheet, responsive.touchTargets)}>
        <DialogBody>
          <DialogTitle>Line {lineNumber}</DialogTitle>
          <DialogContent>
            <div className={styles.sheetFields}>
              <Field label="Item description" required {...validation('itemDescription')}>
                <Input
                  ref={itemDescriptionRef}
                  id={fieldId('itemDescription')}
                  value={line.itemDescription}
                  onChange={(_, data) => onChange({ itemDescription: data.value })}
                  onBlur={() => onTouch('itemDescription')}
                  onKeyDown={commitOnEnter}
                />
              </Field>
              <Field label="Procurement category" {...validation('procurementCategoryId')}>
                {/* Inside a Field: label and invalid state come from the Field itself. */}
                <CategoryCombobox
                  id={fieldId('procurementCategoryId')}
                  listboxClassName={styles.wideListboxPopup}
                  invalid={false}
                  categories={categories}
                  selectedId={line.procurementCategoryId}
                  onSelect={(procurementCategoryId) => onChange({ procurementCategoryId })}
                  onBlur={() => onTouch('procurementCategoryId')}
                />
              </Field>
              <Field label="Quantity" required {...validation('quantity')}>
                <Input
                  id={fieldId('quantity')}
                  value={line.quantity}
                  onChange={(_, data) => onChange({ quantity: data.value })}
                  onBlur={() => onTouch('quantity')}
                  onKeyDown={commitOnEnter}
                />
              </Field>
              <Field label="Unit" required {...validation('unitOfMeasure')}>
                <FilterableCombobox
                  id={fieldId('unitOfMeasure')}
                  listbox={{ className: styles.wideListboxPopup }}
                  placeholder={units.phase === 'loading' ? 'Loading…' : 'Select'}
                  disabled={units.phase !== 'ready'}
                  options={unitOptions(units)}
                  selectedValue={line.unitOfMeasure}
                  fallbackLabel={unitDisplayLabel(units, line.unitOfMeasure)}
                  onSelect={(unitOfMeasure) => onChange({ unitOfMeasure })}
                  onBlur={() => onTouch('unitOfMeasure')}
                />
              </Field>
              <Field label="Unit price" required {...validation('netPrice')}>
                <Input
                  id={fieldId('netPrice')}
                  value={line.netPrice}
                  onChange={(_, data) => onChange({ netPrice: data.value })}
                  onBlur={() => onTouch('netPrice')}
                  onKeyDown={commitOnEnter}
                />
              </Field>
              <Field label="Delivery date" required {...validation('deliveryDate')}>
                <DatePicker
                  id={fieldId('deliveryDate')}
                  value={line.deliveryDate}
                  minDate={startOfToday()}
                  onSelectDate={(date) => {
                    onChange({ deliveryDate: date ?? null });
                    onTouch('deliveryDate');
                  }}
                />
              </Field>
              <Field label="Start date" {...validation('startDate')}>
                <DatePicker
                  id={fieldId('startDate')}
                  value={line.startDate}
                  onSelectDate={(date) => {
                    onChange({ startDate: date ?? null });
                    onTouch('startDate');
                  }}
                />
              </Field>
              <Field label="End date" {...validation('endDate')}>
                <DatePicker
                  id={fieldId('endDate')}
                  value={line.endDate}
                  onSelectDate={(date) => {
                    onChange({ endDate: date ?? null });
                    onTouch('endDate');
                  }}
                />
              </Field>
              <Field label="Sales tax" {...validation('salesTaxGroupId')}>
                <SalesTaxCombobox
                  id={fieldId('salesTaxGroupId')}
                  salesTaxGroups={salesTaxGroups}
                  selectedId={line.salesTaxGroupId}
                  onSelect={(salesTaxGroupId) => onChange({ salesTaxGroupId })}
                  onBlur={() => onTouch('salesTaxGroupId')}
                />
              </Field>
              <Body1Strong as="p" className={styles.numericCell}>
                Total {total ? formatTotal(total, currencyCode) : '—'}
              </Body1Strong>
            </div>
          </DialogContent>
          <DialogActions>
            {/* Every edit is already applied to the line as it is typed, and
                auto-save runs on its own schedule — so there is nothing to
                commit here and no Cancel that could honestly discard. */}
            <Button appearance="primary" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Shape shared by every Zod issue we read — deliberately loose, not tied to a zod-internal type. */
interface ValidationIssue {
  path: readonly PropertyKey[];
  message: string;
}

/**
 * Maps a Zod issue path to the `id` of its matching field control, so an
 * error-summary link (#32/R2b) can focus the offending field directly — the
 * array-level "Add at least one line item" issue has nothing to focus, so it
 * points at the Add line button instead.
 */
export function fieldElementId(path: readonly PropertyKey[]): string {
  if (path.length === 1 && path[0] === 'lines') return 'field-lines-add-button';
  if (path.length === 1) return `field-${String(path[0])}`;
  if (path.length === 3 && path[0] === 'lines') {
    return `field-lines-${String(path[1])}-${String(path[2])}`;
  }
  return `field-${path.map(String).join('-')}`;
}

function issueAt(
  issues: readonly ValidationIssue[],
  path: readonly PropertyKey[],
): string | undefined {
  return issues.find(
    (issue) => issue.path.length === path.length && issue.path.every((seg, i) => seg === path[i]),
  )?.message;
}

export interface RequestFormProps {
  /**
   * The signed-in viewer's own Entra object id (#222), threaded down from
   * `App.tsx`'s session — compared against the loaded request's
   * `requesterAadId` so a reviewer opening someone else's request through the
   * new role-aware Dashboard can never see edit controls, even at a status
   * (REJECTED) that is normally editable for its actual owner. `null`/omitted
   * — every existing caller and test — keeps today's purely status-based
   * behaviour: a request's real owner is always trusted, and a brand-new
   * `/requests/new` draft (no `requesterAadId` yet) is always editable.
   */
  /**
   * `'review'` — a reviewer editing someone else's PENDING_REVIEW request at
   * `/review/:id/edit`: autosave off, "Save changes" instead of "Submit for
   * review", attachments read-only. The default, `'requester'`, is the
   * existing form.
   */
  mode?: 'requester' | 'review';
  viewerAadId?: string | null;
}

export function RequestForm({ viewerAadId = null, mode = 'requester' }: RequestFormProps = {}) {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const isNarrow = useIsNarrowViewport();
  const [companies, setCompanies] = useState<Rows<CompanyRow>>({ phase: 'loading' });
  const [company, setCompany] = useState<string | null>(null);

  // SOW §4.2's remaining header fields (#29). Purchase type defaults to
  // Regular; projectId is retained across a Regular↔Capital toggle (cleared
  // only on save, per the wireframe note) rather than wiped the moment
  // Capital is deselected — there is no save action to clear it against yet,
  // since draft auto-save wiring is a separate ticket.
  const [purchaseType, setPurchaseType] = useState<PurchaseType>('REGULAR');
  const [projectId, setProjectId] = useState('');
  const [projectCategoryId, setProjectCategoryId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [notesForVendor, setNotesForVendor] = useState('');

  const loadCompanies = useCallback(async (signal?: AbortSignal) => {
    setCompanies({ phase: 'loading' });
    try {
      const rows = await getCompanies();
      if (signal?.aborted) return;
      setCompanies({ phase: 'ready', rows });
    } catch {
      if (signal?.aborted) return;
      setCompanies({ phase: 'error' });
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadCompanies(controller.signal);
    return () => controller.abort();
  }, [loadCompanies]);
  const retryCompanies = useCallback(() => void loadCompanies(), [loadCompanies]);
  useAutoRetry(companies.phase, retryCompanies);

  const costCentres = useDependentField<DimensionRow>(company, getCostCentres);
  const lineOfBusiness = useDependentField<DimensionRow>(company, getLineOfBusiness);
  const warehouses = useDependentField<WarehouseRow>(company, getWarehouses);
  const salesTaxGroups = useDependentField<SalesTaxGroupRow>(company, getSalesTaxGroups);
  const projectCategories = useDependentField<ProjectCategoryRow>(company, getProjectCategories);
  const mainAccounts = useDependentField<MainAccountRow>(company, getMainAccounts);

  // Chosen each time and never defaulted: a wrong default would post to the wrong ledger account.
  const [mainAccountId, setMainAccountId] = useState<string | null>(null);
  // False when the deployment fixes the main account in its dimension format (learned from the
  // saved request) — then there is nothing to choose, so the field is not offered.
  const [mainAccountRequired, setMainAccountRequired] = useState(true);
  const [costCentreId, setCostCentreId] = useState<string | null>(null);
  const [lineOfBusinessId, setLineOfBusinessId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [showCompanyChangeWarning, setShowCompanyChangeWarning] = useState(false);

  const [vendorQuery, setVendorQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState<VendorRow | null>(null);
  const vendorSearch = useVendorSearch(
    vendorQuery,
    selectedVendor ? vendorLabel(selectedVendor) : null,
    company,
  );
  useAutoRetry(vendorSearch.state.phase, vendorSearch.retry);
  // R2d note 4 / behaviour rule 8 — set only when a vendor swap actually
  // changes the currency lines were priced in; cleared on dismiss or on the
  // next vendor pick that doesn't reintroduce the mismatch.
  const [vendorCurrencyWarning, setVendorCurrencyWarning] = useState<string | null>(null);
  // The vendor Field's own `onChange` nulls `selectedVendor` the instant the
  // user starts typing to search again (so the stale selection doesn't linger
  // as a "match" for the new query) — same shape as `useVendorSearch`'s own
  // `selectedLabelRef` a few lines up. That means by the time a *second*
  // `handleVendorSelect` fires, `selectedVendor` no longer holds the vendor
  // being replaced. This ref does, updated only inside `handleVendorSelect`
  // itself, so the currency comparison always has a real "previous vendor" to
  // compare against.
  const lastSelectedVendorRef = useRef<VendorRow | null>(null);

  const categories = useOnceFetched<ProcurementCategoryRow>(getProcurementCategories);
  const units = useOnceFetched<UnitRow>(getUnits);
  useAutoRetry(categories.state.phase, categories.retry);
  useAutoRetry(units.state.phase, units.retry);
  // Live D365 Projects combobox (#160) — only fetched once Capital is
  // selected, per masterdata.ts: "only needed once the purchase type is
  // Capital, where a project is mandatory."
  // Projects are per-company in F&O — re-fetched whenever the company changes.
  const fetchProjects = useCallback(() => getProjects(company), [company]);
  const projects = useEnabledOnceFetched<ProjectRow>(purchaseType === 'CAPITAL', fetchProjects);
  useAutoRetry(projects.state.phase, projects.retry);
  // #248 (wireframe R3b) — a brand-new draft starts with NO lines at all, not
  // two pre-seeded blank ones: `hydrateFromRequest` below is the only other
  // writer of this state, and it runs solely for `/requests/:id` (an existing
  // draft or a rejected request being resubmitted), never for `/requests/new`
  // — see its own docblock. So this literal only ever fires once, on the very
  // first render of a request nobody has saved anything into yet.
  const [lines, setLines] = useState<LineItemDraft[]>([]);
  const [touchedByLine, setTouchedByLine] = useState<Record<string, Set<LineItemField>>>({});
  /** Which line the mobile full-screen sheet is editing — never used above the breakpoint. */
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [removedLine, setRemovedLine] = useState<{ line: LineItemDraft; index: number } | null>(
    null,
  );

  /**
   * K1 "Add line → The new row's Item description, already in edit."
   *
   * The focus has to wait for the row (or, below the breakpoint, the sheet) to
   * exist, so the id is parked here and the effect below claims it once the
   * control is in the DOM.
   */
  const [pendingFocusLineId, setPendingFocusLineId] = useState<string | null>(null);

  function addLine() {
    const line = newLine(deliveryDate);
    setLines((prev) => [...prev, line]);
    setPendingFocusLineId(line.id);
  }

  function updateLine(id: string, patch: Partial<LineItemDraft>) {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  }

  function touchLineField(id: string, field: LineItemField) {
    setTouchedByLine((prev) => {
      const existing = prev[id];
      if (existing?.has(field)) return prev;
      const next = new Set(existing);
      next.add(field);
      return { ...prev, [id]: next };
    });
  }

  function duplicateLine(id: string) {
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === id);
      if (index === -1) return prev;
      nextLineSeq += 1;
      const copy: LineItemDraft = { ...prev[index], id: `line-${nextLineSeq}` };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
  }

  function moveLine(id: string, direction: -1 | 1) {
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === id);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  }

  // Row overflow's "Remove" recovers via an inline undo banner rather than a
  // confirm dialog, per the wireframe. The line is removed immediately
  // (optimistic); undo re-inserts it at its original position, and the
  // banner itself clears — nothing left to finalize — once the window lapses.
  // 10s per wireframe sheet 10.1 (D3) — the same duration as every other
  // undo-toast in the app (attachment removal included).
  const REMOVE_UNDO_MS = 10000;

  function removeLine(id: string) {
    // A sheet open on the line being removed has nothing left to edit.
    setEditingLineId((current) => (current === id ? null : current));
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === id);
      if (index === -1) return prev;
      setRemovedLine({ line: prev[index], index });
      return prev.filter((line) => line.id !== id);
    });
  }

  useEffect(() => {
    if (!removedLine) return undefined;
    const timer = setTimeout(() => setRemovedLine(null), REMOVE_UNDO_MS);
    return () => clearTimeout(timer);
  }, [removedLine]);

  /**
   * K1 "Remove line → The undo toast, so it is reachable without a mouse", and
   * D3's keyboard rule: "Focus moves to the toast; Esc dismisses without
   * undoing." A document listener rather than an `onKeyDown`, so Esc works
   * wherever focus has since moved — the same shape `AttachmentsSection`
   * already uses for its own removal toast.
   */
  const undoToastRef = useRef<HTMLOutputElement>(null);
  useEffect(() => {
    if (!removedLine) return undefined;
    undoToastRef.current?.focus();
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') setRemovedLine(null);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [removedLine]);

  function undoRemoveLine() {
    setRemovedLine((current) => {
      if (!current) return current;
      setLines((prev) => {
        const next = [...prev];
        next.splice(current.index, 0, current.line);
        return next;
      });
      return null;
    });
  }

  const grandTotal = useMemo(() => sumLineTotals(lines.map((line) => lineTotal(line))), [lines]);

  const editingLineIndex = lines.findIndex((line) => line.id === editingLineId);
  const editingLine = editingLineIndex === -1 ? undefined : lines[editingLineIndex];

  // K1 "Dialog closes": leaving the mobile line sheet returns to the Edit
  // button on the card that opened it, not to the top of the page.
  useRestoreFocusOnClose(isNarrow && editingLine !== undefined);

  useEffect(() => {
    if (!pendingFocusLineId) return;
    const index = lines.findIndex((line) => line.id === pendingFocusLineId);
    if (index === -1) {
      setPendingFocusLineId(null);
      return;
    }
    // Below the breakpoint the row has no inline controls at all, so "already
    // in edit" means opening the sheet — which focuses its own Item
    // description on mount.
    if (isNarrow) {
      setEditingLineId(pendingFocusLineId);
      setPendingFocusLineId(null);
      return;
    }
    document.getElementById(`field-lines-${String(index)}-itemDescription`)?.focus();
    setPendingFocusLineId(null);
  }, [pendingFocusLineId, lines, isNarrow]);

  // Auto-save (#31, wireframe R7): fires on field blur and every 30s while
  // dirty — never per keystroke. `dirtyRef`/`savingRef` are refs rather than
  // state because they're read from timer/event callbacks that must always
  // see the latest value, not the one captured when the callback was created.
  const navigate = useNavigate();

  // #197, wireframe R2/R3/R4/R5 — the four-step wizard. All four steps are
  // reachable at any time via the tab row (R2 note 7: "Step tabs, not a
  // wizard… Submit is gated on validation, not on step order") — `step` only
  // controls which single step's content is mounted, never which ones are
  // clickable. Each of the four content blocks below is unchanged from the
  // single-scroll page this replaces; only their container changed.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  // The step row scrolls sideways on a narrow screen; keep the selected step visible in it.
  // Scoped to the row — the app's own top-level tabs are also `role=tab`.
  const stepRailRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stepRailRef.current
      ?.querySelector('[role="tab"][aria-selected="true"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [step]);
  // The `Edit` links on Review & submit, and every validation-summary issue
  // link, both need to switch to a step whose content is not mounted yet
  // before they can focus anything inside it — this parks the target's id
  // until the step-change render has actually mounted it. Keyed on `step` as
  // well as the id itself so a second link to a field on the *same* step (no
  // `step` change, so no re-render to hang the effect on) still re-runs it.
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingFocusId) return;
    const element = document.getElementById(pendingFocusId);
    if (!element) return;
    element.scrollIntoView({ block: 'start' });
    element.focus();
    setPendingFocusId(null);
  }, [pendingFocusId, step]);

  const [draftId, setDraftId] = useState<string | undefined>(undefined);
  const [requestNo, setRequestNo] = useState<number | undefined>(undefined);
  // #144 — a request stops being editable the moment it leaves DRAFT/REJECTED
  // (PENDING_REVIEW/APPROVED/PR_CREATED are all decided or in someone else's
  // hands). `null` means "new draft, not loaded from the API" — always
  // editable. `isEditableRef` mirrors this for the timer/event callbacks
  // below that must never resume autosaving a request the requester can no
  // longer change, even if one was already in flight when the status loaded.
  const [requestStatus, setRequestStatus] = useState<RequestStatus | null>(null);
  const [d365PrNumber, setD365PrNumber] = useState<string | null>(null);
  // #222 — who actually raised the loaded request, `null` until it's loaded
  // (or for a brand-new draft, which never leaves this null and is always
  // editable regardless of `viewerAadId`).
  const [requesterAadId, setRequesterAadId] = useState<string | null>(null);
  // Permissive unless BOTH sides of the comparison are known and disagree:
  // `viewerAadId === null` means the caller didn't pass one (every existing
  // route into this form except the reviewer Dashboard's "View" action), and
  // `requesterAadId === null` means no request has loaded yet — neither is
  // "not the owner", so this only ever narrows `isEditable`, never widens it.
  const isOwner = viewerAadId === null || requesterAadId === null || viewerAadId === requesterAadId;
  const isReviewMode = mode === 'review';
  // Review mode edits someone else's request, so the reviewer is never its owner
  // — and it is only ever editable while the request is still awaiting review.
  const viewerIsRequester =
    viewerAadId !== null && requesterAadId !== null && viewerAadId === requesterAadId;
  const isEditable = isReviewMode
    ? requestStatus === null || (requestStatus === 'PENDING_REVIEW' && !viewerIsRequester)
    : requestStatus === null ||
      (isOwner && (requestStatus === 'DRAFT' || requestStatus === 'REJECTED'));
  const isEditableRef = useRef(isEditable);
  isEditableRef.current = isEditable;
  const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const autosaveStatusRef = useRef(autosaveStatus);
  autosaveStatusRef.current = autosaveStatus;
  // Set directly inside `runSave` (not just mirrored from `draftId` on render)
  // so a caller that `await`s `runSave()` sees the fresh id immediately,
  // rather than the stale value from whatever render created its closure.
  const draftIdRef = useRef<string | undefined>(undefined);

  function buildDraftPayload(): DraftRequestInput {
    return {
      id: draftId,
      purchaseType,
      projectId: projectId.trim() || undefined,
      projectCategoryId: projectCategoryId.trim() || undefined,
      companyCode: company ?? undefined,
      mainAccountId: mainAccountId ?? undefined,
      costCentreId: costCentreId ?? undefined,
      lineOfBusinessId: lineOfBusinessId ?? undefined,
      warehouseId: warehouseId ?? undefined,
      vendorAccount: selectedVendor?.accountNumber,
      paymentTerms: selectedVendor?.paymentTerms,
      currencyCode: selectedVendor?.currencyCode,
      deliveryDate: toIsoDate(deliveryDate),
      internalNotes: internalNotes.trim() || undefined,
      notesForVendor: notesForVendor.trim() || undefined,
      lines: lines.map(lineToDraftPayload),
    };
  }

  // Always holds the latest `buildDraftPayload` closure so the stable
  // `runSave` below (and the timers/listeners that call it) read current
  // field values instead of whatever was in scope when they were created.
  const buildPayloadRef = useRef(buildDraftPayload);
  buildPayloadRef.current = buildDraftPayload;

  const runSave = useCallback(async () => {
    // A stray timer/blur/online-retry firing after the request loaded and
    // turned out not to be editable must not resurrect autosave against it.
    if (!isEditableRef.current) return;
    // A reviewer's edits are saved explicitly ("Save changes"), never as a draft:
    // autosave would write to the requester's draft endpoint.
    if (isReviewMode) return;
    if (savingRef.current) return;
    savingRef.current = true;
    autosaveStatusRef.current = 'saving';
    setAutosaveStatus('saving');
    try {
      const result = await saveDraft(buildPayloadRef.current());
      setDraftId(result.id);
      draftIdRef.current = result.id;
      setRequestNo(result.requestNo);
      setLastSavedAt(new Date(result.updatedAt));
      dirtyRef.current = false;
      autosaveStatusRef.current = 'idle';
      setAutosaveStatus('idle');
    } catch {
      autosaveStatusRef.current = 'error';
      setAutosaveStatus('error');
    } finally {
      savingRef.current = false;
    }
  }, [isReviewMode]);

  // Attachments upload against the draft immediately on drop (wireframe 03.1
  // note 3), so a request with no id yet needs a save forced first.
  const ensureDraftId = useCallback(async () => {
    if (draftIdRef.current) return draftIdRef.current;
    await runSave();
    return draftIdRef.current;
  }, [runSave]);

  // Review & submit (#35, wireframe R5) — the same data shown in the same
  // order as the reviewer's own view (V2), read-only, resolved from the same
  // D365 rows the editable fields above already fetched.
  const categoryDisplay = useCallback(
    (id: string | null): string => resolveCategoryLabel(categories.state, id),
    [categories.state],
  );

  const reviewDetails: ReviewDetailField[] = useMemo(
    () => [
      { label: 'Type of purchase', value: purchaseType === 'CAPITAL' ? 'Capital' : 'Regular' },
      { label: 'Company', value: company ?? '' },
      ...(mainAccountRequired
        ? [
            {
              label: 'Main account',
              value: resolveDependentLabel(
                mainAccounts.state,
                mainAccountId,
                (row: MainAccountRow) => row.code,
                (row: MainAccountRow) => codeLabel(row.code, row.description),
              ),
            },
          ]
        : []),
      {
        label: 'Cost centre',
        value: resolveDependentLabel(costCentres.state, costCentreId, dimensionKey, dimensionLabel),
      },
      {
        label: 'Line of business',
        value: resolveDependentLabel(
          lineOfBusiness.state,
          lineOfBusinessId,
          dimensionKey,
          dimensionLabel,
        ),
      },
      {
        label: 'Warehouse',
        value: resolveDependentLabel(warehouses.state, warehouseId, warehouseKey, warehouseLabel),
      },
      { label: 'Vendor', value: selectedVendor?.name ?? '' },
      { label: 'Delivery date', value: formatReviewDate(deliveryDate) },
      {
        label: 'Project',
        value: purchaseType === 'CAPITAL' ? projectId : 'Not applicable — Regular',
      },
      ...(purchaseType === 'CAPITAL'
        ? [
            {
              label: 'Project category',
              value: salesTaxLabel(projectCategories.state, projectCategoryId || null),
            },
          ]
        : []),
      { label: 'Internal notes', value: internalNotes },
      { label: 'Notes for vendor', value: notesForVendor },
    ],
    [
      purchaseType,
      company,
      mainAccountRequired,
      mainAccountId,
      mainAccounts.state,
      costCentreId,
      costCentres.state,
      lineOfBusinessId,
      lineOfBusiness.state,
      warehouseId,
      warehouses.state,
      selectedVendor,
      deliveryDate,
      projectId,
      projectCategoryId,
      projectCategories.state,
      internalNotes,
      notesForVendor,
    ],
  );

  const reviewLines: ReviewLine[] = useMemo(
    () =>
      lines.map((line) => ({
        id: line.id,
        itemDescription: line.itemDescription,
        categoryLabel: categoryDisplay(line.procurementCategoryId),
        quantity: line.quantity,
        unitOfMeasure: line.unitOfMeasure ?? '',
        // Raw, unformatted decimal strings — `ReviewSummary` applies the shared
        // #148 formatters itself (grouping + decimal precision) using its own
        // `currencyCode` prop, so the formatting rule lives in exactly one place.
        netPrice: line.netPrice,
        lineTotal: lineTotal(line)?.toString() ?? '',
        deliveryDate: formatReviewDate(line.deliveryDate),
        startDate: formatReviewDate(line.startDate),
        endDate: formatReviewDate(line.endDate),
        salesTaxLabel: salesTaxLabel(salesTaxGroups.state, line.salesTaxGroupId),
      })),
    [lines, categoryDisplay, salesTaxGroups.state],
  );

  const isFirstDirtyCheck = useRef(true);
  useEffect(() => {
    if (isFirstDirtyCheck.current) {
      isFirstDirtyCheck.current = false;
      return;
    }
    dirtyRef.current = true;
  }, [
    purchaseType,
    projectId,
    projectCategoryId,
    company,
    mainAccountId,
    costCentreId,
    lineOfBusinessId,
    warehouseId,
    selectedVendor,
    deliveryDate,
    internalNotes,
    notesForVendor,
    lines,
  ]);

  // Validation (#32, wireframe R2b): the same shared Zod schema used to
  // gate submission server-side, re-run client-side on every relevant
  // change so its literal messages can be shown inline and in the summary —
  // the API remains the actual control, this is only ever a courtesy.
  const submitValidation = useMemo(
    () => submitRequestSchema.safeParse(buildSubmitPayload()),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildSubmitPayload closes over exactly these fields
    [
      purchaseType,
      projectId,
      projectCategoryId,
      company,
      mainAccountId,
      costCentreId,
      lineOfBusinessId,
      warehouseId,
      selectedVendor,
      deliveryDate,
      internalNotes,
      notesForVendor,
      lines,
    ],
  );
  const rawIssues = submitValidation.success ? [] : submitValidation.error.issues;
  const noVendorPicked = rawIssues.some(
    (issue) => issue.path.length === 1 && issue.path[0] === 'vendorAccount',
  );
  const issues: ValidationIssue[] = rawIssues.filter(
    (issue) =>
      !(noVendorPicked && issue.path.length === 1 && HIDDEN_ISSUE_PATHS.has(String(issue.path[0]))),
  );

  function buildSubmitPayload() {
    return {
      purchaseType,
      projectId: projectId.trim() || undefined,
      projectCategoryId: projectCategoryId.trim() || undefined,
      companyCode: company ?? '',
      mainAccountId: mainAccountId ?? '',
      costCentreId: costCentreId ?? '',
      lineOfBusinessId: lineOfBusinessId ?? '',
      warehouseId: warehouseId ?? '',
      vendorAccount: selectedVendor?.accountNumber ?? '',
      paymentTerms: selectedVendor?.paymentTerms ?? '',
      currencyCode: selectedVendor?.currencyCode ?? '',
      deliveryDate: toIsoDate(deliveryDate) ?? '',
      internalNotes: internalNotes.trim() || undefined,
      notesForVendor: notesForVendor.trim() || undefined,
      lines: lines.map((line) => ({
        itemDescription: line.itemDescription.trim(),
        procurementCategoryId: line.procurementCategoryId ?? '',
        quantity: line.quantity,
        unitOfMeasure: line.unitOfMeasure ?? '',
        netPrice: line.netPrice,
        deliveryDate: toIsoDate(line.deliveryDate) ?? '',
        startDate: toIsoDate(line.startDate),
        endDate: toIsoDate(line.endDate),
        salesTaxGroupId: line.salesTaxGroupId || undefined,
        vendorItemDescription: line.vendorItemDescription.trim() || undefined,
        vendorPartNo: line.vendorPartNo.trim() || undefined,
      })),
    };
  }

  const [touchedHeaderFields, setTouchedHeaderFields] = useState<ReadonlySet<string>>(new Set());
  /**
   * The field K1's "form opens" rule put focus on. Leaving it without editing
   * it must not mark it touched: the user never chose to visit it, and
   * greeting them with "Company is required" on a field they have not typed in
   * is exactly the noise R2b's touched-based display exists to avoid.
   */
  const untouchableOnBlur = useRef<string | null>(null);
  function touchHeaderField(key: string) {
    if (untouchableOnBlur.current === key) {
      untouchableOnBlur.current = null;
      return;
    }
    setTouchedHeaderFields((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  }

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);

  // Edit targets for the review screen below (#35) — see `focusSection`.
  const detailsSectionRef = useRef<HTMLDivElement>(null);
  const lineItemsSectionRef = useRef<HTMLDivElement>(null);
  const attachmentsSectionRef = useRef<HTMLDivElement>(null);

  const [uploadedAttachments, setUploadedAttachments] = useState<Attachment[]>([]);
  // #260 — the freshly-uploaded `File` objects behind `uploadedAttachments`,
  // keyed by attachment id. Before this, `AttachmentsSection`'s own
  // `onAttachmentsChange` collapsed each `AttachmentItem` down to its plain
  // `Attachment`, discarding `localFile` — so `ReviewSummary`, one step
  // later, had no choice but to re-fetch the image over the authenticated
  // download route, and a transient failure there left the thumbnail blank
  // forever. Kept alongside `uploadedAttachments` and passed straight through
  // to `ReviewSummary` so it never has to guess.
  const [uploadedAttachmentLocalFiles, setUploadedAttachmentLocalFiles] = useState<
    Map<string, File>
  >(new Map());

  // #42 — loading an existing request by id (a rejection, most commonly, but
  // this works for any status Dashboard ever links to). `/requests/new` has
  // no :id, so `routeId` is undefined there and none of this runs — the form
  // behaves exactly as it always has for a brand-new draft.
  const { id: routeId } = useParams<{ id: string }>();
  const [loadPhase, setLoadPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    routeId ? 'loading' : 'idle',
  );
  const [originalValues, setOriginalValues] = useState<Record<string, string> | null>(null);
  // #155, wireframe R8 — `section` is the tag `formatRejectionSection` writes
  // onto the audit comment (parsed back out below), naming which part of the
  // request the reviewer flagged; `null` for a rejection that predates the
  // feature or simply didn't name one.
  const [rejection, setRejection] = useState<{
    actorName: string;
    comment: string;
    section: RejectionSection | null;
  } | null>(null);
  const [auditHistory, setAuditHistory] = useState<RequestAuditEntry[]>([]);

  // Default the three optional fields to the first value D365 offers — ONCE per
  // company (cost centre, line of business) or per line (category), when the
  // list first becomes available. Never again after that, so a requester who
  // deliberately clears one (to leave it for the reviewer) is not overridden.
  // Requester mode only: a reviewer editing a request must see exactly what was
  // submitted.
  // `loadedFor === company` matters — right after a company change the list in
  // state still belongs to the OLD company for one render, and defaulting from
  // it would select a cost centre that does not exist in the new company.
  // `idle` is a brand-new draft (nothing to load); `ready` is a loaded request.
  const defaultsEnabled =
    !isReviewMode && isEditable && (loadPhase === 'idle' || loadPhase === 'ready');
  const costCentreDefaultedFor = useRef<string | null>(null);
  const lineOfBusinessDefaultedFor = useRef<string | null>(null);
  const categoryDefaultedLines = useRef(new Set<string>());

  useEffect(() => {
    if (!defaultsEnabled) return;
    const { state, loadedFor } = costCentres;
    if (loadedFor !== company || state.phase !== 'ready') return;
    if (costCentreDefaultedFor.current === company) return;
    costCentreDefaultedFor.current = company;
    // The first one the dropdown lists, not the first D365 happened to return.
    const first = firstInOrder(state.rows, dimensionLabel);
    if (costCentreId === null && first) setCostCentreId(dimensionKey(first));
  }, [defaultsEnabled, costCentreId, costCentres, company]);

  useEffect(() => {
    if (!defaultsEnabled) return;
    const { state, loadedFor } = lineOfBusiness;
    if (loadedFor !== company || state.phase !== 'ready') return;
    if (lineOfBusinessDefaultedFor.current === company) return;
    lineOfBusinessDefaultedFor.current = company;
    const first = firstInOrder(state.rows, dimensionLabel);
    if (lineOfBusinessId === null && first) setLineOfBusinessId(dimensionKey(first));
  }, [defaultsEnabled, lineOfBusinessId, lineOfBusiness, company]);

  useEffect(() => {
    if (!defaultsEnabled || categories.state.phase !== 'ready') return;
    const fresh = lines.filter((line) => !categoryDefaultedLines.current.has(line.id));
    if (fresh.length === 0) return;
    for (const line of fresh) categoryDefaultedLines.current.add(line.id);
    const first = firstInOrder(categories.state.rows, categoryLabel);
    if (!first) return;
    const key = categoryKey(first);
    const needsDefault = new Set(
      fresh.filter((line) => !line.procurementCategoryId).map((line) => line.id),
    );
    if (needsDefault.size === 0) return;
    setLines((previous) =>
      previous.map((line) =>
        needsDefault.has(line.id) ? { ...line, procurementCategoryId: key } : line,
      ),
    );
  }, [defaultsEnabled, categories.state, lines]);

  const loadRequest = useCallback(async (id: string, signal?: AbortSignal) => {
    setLoadPhase('loading');
    try {
      const request = await getRequest(id);
      if (signal?.aborted) return;
      hydrateFromRequest(request);
      setLoadPhase('ready');
    } catch {
      if (signal?.aborted) return;
      setLoadPhase('error');
    }

    // Populates every piece of form state from a fetched request — used only
    // when opening an existing one (never for a fresh /requests/new draft).
    function hydrateFromRequest(request: RequestDetail) {
      setDraftId(request.id);
      draftIdRef.current = request.id;
      setRequestNo(request.requestNo);
      setRequestStatus(request.status);
      setRequesterAadId(request.requesterAadId);
      setD365PrNumber(request.d365PrNumber);
      setPurchaseType(request.purchaseType === 'CAPITAL' ? 'CAPITAL' : 'REGULAR');
      setProjectId(request.projectId ?? '');
      setProjectCategoryId(request.projectCategoryId ?? '');
      setCompany(request.companyCode);
      setMainAccountId(request.mainAccountId);
      setMainAccountRequired(request.mainAccountRequired !== false);
      setCostCentreId(request.costCentreId);
      setLineOfBusinessId(request.lineOfBusinessId);
      // What was saved is what the requester chose — including leaving these
      // empty. Mark the restored company and lines as already handled so the
      // first-value defaults only ever fill a NEW draft, a new line, or a
      // company the requester picks now.
      costCentreDefaultedFor.current = request.companyCode;
      lineOfBusinessDefaultedFor.current = request.companyCode;
      for (const line of request.lines) categoryDefaultedLines.current.add(line.id);
      setWarehouseId(request.warehouseId);
      setDeliveryDate(request.deliveryDate ? fromDateOnly(request.deliveryDate) : null);
      setInternalNotes(request.internalNotes ?? '');
      setNotesForVendor(request.notesForVendor ?? '');
      setLines(
        request.lines.length > 0
          ? request.lines.map((line) => ({
              id: line.id,
              itemDescription: line.itemDescription ?? '',
              procurementCategoryId: line.procurementCategoryId,
              quantity: line.quantity ?? '',
              unitOfMeasure: line.unitOfMeasure,
              netPrice: line.netPrice ?? '',
              deliveryDate: line.deliveryDate ? fromDateOnly(line.deliveryDate) : null,
              startDate: line.startDate ? fromDateOnly(line.startDate) : null,
              endDate: line.endDate ? fromDateOnly(line.endDate) : null,
              salesTaxGroupId: line.salesTaxGroupId,
              vendorItemDescription: line.vendorItemDescription ?? '',
              vendorPartNo: line.vendorPartNo ?? '',
            }))
          : [newLine(), newLine()],
      );
      setUploadedAttachments(request.attachments);
      setAuditHistory(request.auditLog);

      // Last, not first — a request can only be REJECTED once at a time, but
      // this stays correct if it's ever rejected, resubmitted and rejected again.
      let rejectedEntry: RequestAuditEntry | undefined;
      for (const entry of request.auditLog) {
        if (entry.action === 'REJECTED') rejectedEntry = entry;
      }
      if (rejectedEntry) {
        const { section, comment } = parseRejectionSection(rejectedEntry.comment ?? '');
        setRejection({ actorName: rejectedEntry.actorName, comment, section });
      } else {
        setRejection(null);
      }

      setOriginalValues({
        purchaseType: request.purchaseType === 'CAPITAL' ? 'Capital' : 'Regular',
        projectId: request.projectId ?? '',
        projectCategoryId: request.projectCategoryId ?? '',
        companyCode: request.companyCode ?? '',
        mainAccountId: request.mainAccountId ?? '',
        costCentreId: request.costCentreId ?? '',
        lineOfBusinessId: request.lineOfBusinessId ?? '',
        warehouseId: request.warehouseId ?? '',
        vendorAccount: request.vendorAccount ?? '',
        // Sliced to YYYY-MM-DD — the same short form `toIsoDate` produces for
        // the current value, so an unedited date reads as unchanged.
        deliveryDate: request.deliveryDate?.slice(0, 10) ?? '',
        internalNotes: request.internalNotes ?? '',
        notesForVendor: request.notesForVendor ?? '',
      });

      // The vendor master (name, terms, currency) isn't stored on the request
      // itself — only the account code is — so it's re-resolved by searching
      // on that code. If it can't be found (renamed/deactivated vendor), fall
      // back to the stored terms/currency with the code standing in for a name.
      if (request.vendorAccount) {
        void getVendors(request.vendorAccount, 1, request.companyCode)
          .then((rows) => {
            if (signal?.aborted) return undefined;
            const match = rows.find((row) => row.accountNumber === request.vendorAccount);
            const vendor: VendorRow = match ?? {
              accountNumber: request.vendorAccount ?? '',
              name: request.vendorAccount ?? '',
              paymentTerms: request.paymentTerms ?? '',
              currencyCode: request.currencyCode ?? '',
              city: '',
            };
            setSelectedVendor(vendor);
            setVendorQuery(vendorLabel(vendor));
            return undefined;
          })
          .catch(() => undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (!routeId) return undefined;
    const controller = new AbortController();
    void loadRequest(routeId, controller.signal);
    return () => controller.abort();
  }, [routeId, loadRequest]);

  function headerFieldMessage(key: string): string | undefined {
    return issueAt(issues, [key]);
  }
  function changedHintFor(key: string, current: string) {
    const hint = changedFromHint(originalValues, key, current);
    if (!hint) return null;
    return (
      <Body1 as="p" className={styles.changedHint}>
        {hint}
      </Body1>
    );
  }
  function showHeaderFieldError(key: string): boolean {
    return (touchedHeaderFields.has(key) || submitAttempted) && !!headerFieldMessage(key);
  }
  function lineErrorsFor(index: number): Partial<Record<LineItemField, string>> {
    const result: Partial<Record<LineItemField, string>> = {};
    for (const issue of issues) {
      if (issue.path.length === 3 && issue.path[0] === 'lines' && issue.path[1] === index) {
        result[issue.path[2] as LineItemField] = issue.message;
      }
    }
    return result;
  }
  /**
   * A validation-summary link (top-of-form, or R5's own linked issue list)
   * can name a field on any step, including one not currently mounted — #197
   * added the step switch this needs; everything past it is unchanged from
   * before the wizard.
   */
  function focusField(path: readonly PropertyKey[]) {
    if (path.length >= 1 && path[0] === 'lines') {
      // Below the breakpoint a line's controls only exist while its sheet is
      // open, so an error-summary link would otherwise focus nothing at all —
      // open the sheet that owns the field instead (#53). This applies
      // regardless of which step is active: the sheet is a modal, not a
      // step's own content.
      if (isNarrow && path.length === 3) {
        const target = lines[Number(path[1])];
        if (target) setEditingLineId(target.id);
        return;
      }
      setStep(2);
      setPendingFocusId(fieldElementId(path));
      return;
    }
    setStep(1);
    setPendingFocusId(fieldElementId(path));
  }

  // R5/R2b (#160) — the bottom-of-form review's own invalid-state bar gets the
  // same linked issue list the top-of-form summary already builds, instead of
  // a plain sentence with nothing to click. Only passed to `ReviewSummary`
  // once `submitAttempted` — R5's own sentence promises "Submit for review
  // will list what is missing", so the list appears on the same trigger the
  // top summary already uses, not on every render of an untouched, still-
  // being-filled-in form (which starts invalid with two blank lines and would
  // otherwise show a dozen issues nobody asked to see yet).
  const reviewSummaryIssues: ReviewSummaryIssue[] = issues.map((issue) => ({
    message: issue.message,
    onClick: () => focusField(issue.path),
  }));

  // R5's Edit action "returns to the relevant step tab with that section
  // focused" — #197 replaced the old same-page scroll-and-focus with an
  // actual step switch; `pendingFocusId` (declared near `step` above) waits
  // for that step's content to mount before focusing it.
  function handleEditDetails() {
    setStep(1);
    setPendingFocusId('section-details');
  }
  function handleEditLines() {
    setStep(2);
    setPendingFocusId('section-lines');
  }
  function handleEditAttachments() {
    setStep(3);
    setPendingFocusId('section-attachments');
  }
  // #155 — R8's "Go to this section" reuses the exact same three targets R5's
  // per-section Edit buttons do; only the source of the section differs.
  function focusRejectedSection() {
    if (!rejection?.section) return;
    if (rejection.section === 'HEADER') handleEditDetails();
    else if (rejection.section === 'LINE_ITEMS') handleEditLines();
    else handleEditAttachments();
  }

  async function completeSubmit() {
    await runSave();
    const id = draftIdRef.current;
    if (!id || !submitValidation.success) {
      setSubmitError('Could not save the draft before submitting. Try again.');
      return;
    }
    try {
      await submitRequest(id, submitValidation.data);
      setSubmitted(true);
      // Known outcome of a successful submit, regardless of resubmit vs.
      // fresh — set directly rather than waiting on a refetch, matching
      // what "View request" needs below when it can't rely on a remount.
      setRequestStatus('PENDING_REVIEW');
    } catch {
      setSubmitError('Could not submit for review. Try again.');
    }
  }

  const [savingReview, setSavingReview] = useState(false);
  async function handleSaveReviewerEdits() {
    setSubmitError(null);
    if (!submitValidation.success) {
      if (!submitAttempted) setSubmitAttempted(true);
      else summaryRef.current?.focus();
      return;
    }
    const id = draftIdRef.current;
    if (!id) return;
    setSavingReview(true);
    try {
      await saveReviewerEdits(id, submitValidation.data);
      void navigate(`/review?open=${id}`);
    } catch (error) {
      setSubmitError(
        error instanceof ApiError && error.status === 409
          ? 'This request is no longer awaiting review, so your changes could not be saved.'
          : 'Could not save your changes. Try again.',
      );
      setSavingReview(false);
    }
  }

  function handleSubmitClick() {
    setSubmitError(null);
    if (!submitValidation.success) {
      if (!submitAttempted) {
        setSubmitAttempted(true);
      } else {
        summaryRef.current?.focus();
      }
      return;
    }
    setSubmitAttempted(true);
    void completeSubmit();
  }

  /**
   * K1 "Form opens → First invalid field, else first empty required field,
   * else the heading."
   *
   * An empty required field is itself an invalid one as far as the shared Zod
   * schema is concerned, so a single pass down `issues` — already in schema
   * order, the same order the error summary lists them in — covers both tiers
   * of the rule. Nothing is *shown* as an error by this: the per-field messages
   * still wait for a blur or a submit attempt, so opening the form does not
   * greet the user with red text on a field they have not touched.
   *
   * It waits for the company list, and it waits for an existing request to
   * finish loading: until both have settled every control that could be the
   * answer is still disabled, and focus placed early would have to move again.
   * If the first invalid field is *still* not focusable once they have — the
   * company list failed, so the whole dependent chain is dead — the rule falls
   * through to the heading rather than skipping to some later field the user
   * cannot usefully fill in yet.
   */
  const initialFocusPlaced = useRef(false);
  useEffect(() => {
    if (initialFocusPlaced.current) return;
    if (loadPhase === 'loading' || companies.phase === 'loading') return;
    initialFocusPlaced.current = true;
    // "Form opens" means exactly that. Waiting for the company list means this
    // runs a network round-trip after the first paint, by which point a fast
    // user may already be typing — moving focus out from under them would be
    // worse than not placing it at all.
    const { activeElement } = document;
    if (activeElement && activeElement !== document.body) return;
    const firstIssue = issues[0];
    if (!firstIssue) {
      document.getElementById(HEADING_ELEMENT_ID)?.focus();
      return;
    }
    // #197 — the common case (company code, on step 1, the wizard's own
    // default) focuses synchronously in this same effect, exactly as before
    // the wizard: going through `pendingFocusId`'s extra render/effect round
    // trip here — even though `setPendingFocusId` ultimately calls the same
    // `element.focus()` — widens the window between "the form is ready" and
    // "focus actually lands" by a full render cycle, long enough that a fast
    // test's own click (or a fast real user) can land first and then get its
    // focus silently stolen back once the deferred effect finally runs.
    // Only a target whose step isn't mounted yet (a hydrated/rejected request
    // whose first issue is on step 2) genuinely needs the deferred path,
    // because there is nothing to focus synchronously yet.
    const onLines = firstIssue.path[0] === 'lines';
    if (firstIssue.path.length === 1) {
      untouchableOnBlur.current = String(firstIssue.path[0]);
    }
    if (onLines) {
      setStep(2);
      setPendingFocusId(fieldElementId(firstIssue.path));
      return;
    }
    const element = document.getElementById(fieldElementId(firstIssue.path));
    if (element && !element.hasAttribute('disabled')) {
      element.focus();
    } else {
      document.getElementById(HEADING_ELEMENT_ID)?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, on the render where the form first becomes interactive
  }, [loadPhase, companies.phase]);

  // Focuses the summary the moment it (re-)appears after a failed submit
  // attempt — deliberately keyed on `submitAttempted` alone, not `issues`,
  // so it fires once per press rather than on every keystroke afterward.
  useEffect(() => {
    if (submitAttempted && !submitValidation.success) {
      summaryRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-focus only on a new submit attempt, not every issues recompute
  }, [submitAttempted]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (dirtyRef.current) void runSave();
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [runSave]);

  // Wireframe R7 note 3: "We will keep retrying" — the same continuous-retry
  // pattern every other D365-backed control in this form already uses, not a
  // one-shot attempt. `useAutoRetry` re-arms because a failed retry passes
  // back through 'saving' before landing on 'error' again.
  useAutoRetry(
    autosaveStatus,
    useCallback(() => void runSave(), [runSave]),
  );

  // "Killing the network shows the failure state and recovers on reconnect"
  // (#31's done-when) — retry as soon as the browser reports connectivity,
  // rather than waiting out the rest of the auto-retry delay.
  useEffect(() => {
    function handleOnline() {
      if (autosaveStatusRef.current === 'error') void runSave();
    }
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [runSave]);

  const [showCloseWarning, setShowCloseWarning] = useState(false);
  // K1 "Dialog closes" — back to the Close button that raised the warning.
  useRestoreFocusOnClose(showCloseWarning);
  // K1 "Dialog opens — trapped inside", on the same terms as the reviewer's
  // approve dialog: focus the primary action rather than leaving it to
  // Fluent's first-focusable default, which here would be "Close anyway" —
  // the one button on this dialog that can lose the user's work.
  const retryFromCloseWarningRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (showCloseWarning) retryFromCloseWarningRef.current?.focus();
  }, [showCloseWarning]);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const saveAndCloseButtonRef = useRef<HTMLButtonElement>(null);

  // Moving focus to Close/Save & close blurs whatever field was previously
  // focused, which would otherwise kick off a fresh background save right as
  // the click handler below reads `autosaveStatusRef` — a race that can flip
  // 'error' to 'saving' before Close ever notices the prior failure. Both
  // buttons already decide save behaviour explicitly, so this blur defers to
  // them instead of firing its own save underneath.
  function handleFormBlur(event: FocusEvent<HTMLDivElement>) {
    const nextFocusTarget = event.relatedTarget;
    if (
      nextFocusTarget === closeButtonRef.current ||
      nextFocusTarget === saveAndCloseButtonRef.current
    ) {
      return;
    }
    if (dirtyRef.current) void runSave();
  }

  // D1: "If the last auto-save failed, Close warns first ... — the only case
  // where leaving can cost work." Any other time, auto-save already means
  // nothing is discarded, so Close can leave immediately.
  function handleClose() {
    if (autosaveStatusRef.current === 'error') {
      setShowCloseWarning(true);
      return;
    }
    void navigate('/');
  }

  function handleCloseAnyway() {
    setShowCloseWarning(false);
    void navigate('/');
  }

  function handleRetryFromCloseWarning() {
    setShowCloseWarning(false);
    void runSave();
  }

  async function handleSaveAndClose() {
    await runSave();
    void navigate('/');
  }

  function handleCompanySelect(value: string | undefined) {
    const next = value ?? null;
    // Re-picking the company already selected changes nothing — don't wipe.
    if (next === company) return;
    // Changing the company clears every dependent field, per R2/R2c — the
    // three effects above pick up the new company and refetch independently.
    // The *selections* made against the old company no longer mean anything
    // against the new one, so they're cleared here too, with a warning if
    // there was something to lose (#29). Vendor and project are per-company
    // in F&O too: one from the old company would make D365 reject the
    // requisition header, so they go with the rest.
    const hasTaxGroup = lines.some((line) => line.salesTaxGroupId);
    if (
      costCentreId ||
      lineOfBusinessId ||
      warehouseId ||
      selectedVendor ||
      projectId ||
      projectCategoryId ||
      hasTaxGroup
    ) {
      setShowCompanyChangeWarning(true);
    }
    // Sales tax groups are a company's own setup, so a line's choice goes with the company too.
    if (hasTaxGroup) {
      setLines((prev) => prev.map((line) => ({ ...line, salesTaxGroupId: null })));
    }
    setCostCentreId(null);
    setLineOfBusinessId(null);
    setWarehouseId(null);
    // `lastSelectedVendorRef` is deliberately kept: the currency-change
    // warning compares the next vendor against the one priced in before.
    setSelectedVendor(null);
    setVendorQuery('');
    setProjectId('');
    setProjectCategoryId('');
    setCompany(next);
  }

  function handleVendorSelect(value: string | undefined) {
    const row =
      vendorSearch.state.phase === 'ready'
        ? vendorSearch.state.rows.find((r) => r.accountNumber === value)
        : undefined;
    if (!row) return;
    // R2d note 4 / behaviour rule 8 — a real currency swap only matters once a
    // line has an actual price entered in the old currency; an empty draft or
    // a same-currency vendor swap has nothing to warn about.
    const previous = lastSelectedVendorRef.current;
    const currencyChanged = !!previous && previous.currencyCode !== row.currencyCode;
    const anyLineHasPrice = lines.some((line) => line.netPrice.trim() !== '');
    setVendorCurrencyWarning(
      currencyChanged && anyLineHasPrice && previous
        ? `Line unit prices were entered in ${previous.currencyCode}. This vendor prices in ${row.currencyCode} — review every line's unit price before submitting.`
        : null,
    );
    setSelectedVendor(row);
    setVendorQuery(vendorLabel(row));
    lastSelectedVendorRef.current = row;
  }

  // #245 — on a fresh /requests/new submission `draftId` differs from the
  // (absent) route id, so this navigate changes `location.pathname` and
  // App.tsx's `key={location.pathname}` remounts RequestForm fresh (entry 41).
  // On a resubmit-after-rejection, the form is already mounted at
  // /requests/:id and draftId === routeId already — navigate() is a no-op to
  // the current path, nothing remounts, and this screen would never clear.
  // Resetting `submitted` directly here fixes that case; it's a harmless
  // no-op on the remount path since the new instance starts fresh anyway.
  function handleViewRequest() {
    setSubmitted(false);
    void navigate(`/requests/${String(draftId)}`);
  }

  // Wireframe R6 — a full screen, not a toast: submission is a milestone,
  // and a toast would be gone before it was read. States what happens next
  // and that no notification fires yet (only PR-created and rejection do).
  //
  // Measured, not fluid (#202): two sentences of copy set across a 1800px tab
  // canvas is unreadable, and this is the same milestone shape the reviewer's
  // own "Purchase Requisition created" screen uses.
  if (submitted) {
    return (
      <div
        className={mergeClasses(
          layout.stack,
          layout.measured,
          styles.milestone,
          responsive.touchTargets,
        )}
      >
        <PageHeader title="Submitted for review" />
        <Body1 as="p">
          PR-{requestNo} is now with the review group. You will be notified if it is rejected, and
          again when the Purchase Requisition is created in D365.
        </Body1>
        <ActionBar
          secondary={
            <Button appearance="secondary" onClick={handleViewRequest}>
              View request
            </Button>
          }
          primary={{ label: 'Back to my requests', onClick: () => void navigate('/') }}
        />
      </div>
    );
  }

  const backToRequests = (
    <Button
      className={styles.backButton}
      appearance="transparent"
      onClick={() => void navigate('/')}
    >
      ← My Requests
    </Button>
  );

  if (loadPhase === 'loading') {
    return (
      <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
        {backToRequests}
        <Spinner label="Loading the request…" labelPosition="after" />
      </div>
    );
  }

  if (loadPhase === 'error') {
    return (
      <div className={mergeClasses(layout.stack, layout.measured, responsive.touchTargets)}>
        {backToRequests}
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load this request</MessageBarTitle>
            Try again, or go back and open it from My Requests.
          </MessageBarBody>
          <MessageBarActions>
            <Button onClick={() => routeId && void loadRequest(routeId)}>Try again</Button>
          </MessageBarActions>
        </MessageBar>
      </div>
    );
  }

  /**
   * The audit trail, identical on the read-only and the editable branch — one
   * `Section`, so the block has a real `<h2>` rather than the bare `Subtitle2`
   * span it used to render (#202).
   */
  const historySection = auditHistory.length > 0 && (
    <Section title="History" count={auditHistory.length}>
      <div className={styles.cardStack}>
        {auditHistory.map((entry, index) => {
          // #155 — a REJECTED row's comment may carry the `[section:…]` tag
          // (`formatRejectionSection`); this is a person's history, so it
          // comes off here the same way `PrCreationFailure.tsx` strips
          // `formatPrFailure`'s tag before showing a D365 message.
          const { section, comment } = parseRejectionSection(entry.comment ?? '');
          return (
            <div
              key={`${entry.action}-${entry.createdAt}-${String(index)}`}
              className={styles.historyEntry}
            >
              <Body1>
                {auditActionLabel(entry.action)} — {entry.actorName},{' '}
                {formatHistoryDate(entry.createdAt)}
                {section && (
                  <Badge
                    appearance="outline"
                    color="warning"
                    className={styles.historySectionBadge}
                  >
                    {REJECTION_SECTION_LABELS[section]}
                  </Badge>
                )}
              </Body1>
              {/* Email only — the raw Entra id is never shown (client request).
                  Suppressed for SYSTEM_ACTOR rows, which have no email. */}
              {entry.actorAadId !== 'system' && (
                <Caption1 className={styles.hint}>
                  {entry.actorEmail ?? 'No email on file'}
                </Caption1>
              )}
              {comment && (
                <Body1 as="p" className={styles.hint}>
                  {comment}
                </Body1>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );

  // #144 — everything past this point (autosave, Submit, every input) is the
  // editable DRAFT/REJECTED experience. A request in any other status only
  // ever gets to look, so it renders through a completely separate, static
  // branch rather than the same form with fields disabled piecemeal — the
  // surest way to guarantee nothing in it can still write back.
  if (loadPhase === 'ready' && !isEditable && requestStatus) {
    return (
      <div className={mergeClasses(layout.stack, responsive.touchTargets)}>
        {backToRequests}
        {/* K1's last-resort focus destination. `PageHeader` owns the page's one
            `<h1>` and exposes no id of its own, so the target is this wrapper —
            focusing it lands a screen-reader user on the title, exactly as
            before, without a second heading element competing with it. */}
        <div id={HEADING_ELEMENT_ID} tabIndex={-1}>
          <PageHeader
            title={`PR-${String(requestNo)}`}
            status={<StatusBadge status={requestStatus} />}
          />
        </div>
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>{READONLY_STATUS_MESSAGES[requestStatus]}</MessageBarTitle>
            {d365PrNumber && <>D365 Purchase Requisition: {d365PrNumber}</>}
          </MessageBarBody>
        </MessageBar>

        <ReviewSummary
          isValid
          details={reviewDetails}
          lines={reviewLines}
          grandTotal={grandTotal.toString()}
          currencyCode={selectedVendor?.currencyCode ?? ''}
          attachments={uploadedAttachments}
          readOnly
        />

        {historySection}
      </div>
    );
  }

  // Wireframe R8 — the step-tab row carries a small badge on whichever step
  // the reviewer's tagged rejection section maps to, so the requester can see
  // where the fix belongs before ever opening that step (#197, log entry 51's
  // own R8 sheet: "3. Attachments 1 needed").
  const rejectedStep: 1 | 2 | 3 | null =
    rejection?.section === 'HEADER'
      ? 1
      : rejection?.section === 'LINE_ITEMS'
        ? 2
        : rejection?.section === 'ATTACHMENTS'
          ? 3
          : null;

  function handleTabSelect(_: SelectTabEvent, data: SelectTabData) {
    setStep(Number(data.value) as 1 | 2 | 3 | 4);
  }

  /**
   * The autosave state, in `PageHeader`'s informational `meta` slot — beside
   * the title rather than in a bar of its own (wireframe R7's "Draft saved
   * 14:32"). `Caption1` because it is metadata, and metadata is 12/16 on the
   * type ramp; de-emphasis comes from `PageHeader`'s own foreground token, not
   * from `opacity`.
   */
  const autosaveMeta =
    autosaveStatus === 'saving' ? (
      <Spinner size="tiny" label="Saving…" labelPosition="after" />
    ) : autosaveStatus === 'error' ? (
      <Caption1>Not saved — retrying</Caption1>
    ) : (
      <Caption1>
        {lastSavedAt ? `Draft saved ${formatSavedAt(lastSavedAt)}` : 'Not saved yet'}
      </Caption1>
    );

  return (
    // One stack owns the 16px rhythm between every block on the page; no block
    // sets a margin of its own (#202).
    <div onBlur={handleFormBlur} className={mergeClasses(layout.stack, responsive.touchTargets)}>
      <Button
        className={styles.backButton}
        appearance="transparent"
        onClick={() => void navigate('/')}
      >
        ← Back
      </Button>

      {/* `PageHeader` owns the page's one `<h1>`, the status badge beside it,
          the autosave state in its `meta` slot and the step rail as its
          children. It exposes no id of its own, so K1's last-resort focus
          destination is this wrapper — `tabIndex={-1}` so it can take focus
          without joining the tab order, exactly as the old `Title2` did. */}
      <div id={HEADING_ELEMENT_ID} tabIndex={-1}>
        <PageHeader
          title={requestNo ? `PR-${String(requestNo)}` : 'New request'}
          status={<StatusBadge status={requestStatus ?? 'DRAFT'} />}
          // Nothing is autosaved in review mode, so "Not saved yet" would be false.
          meta={isReviewMode ? undefined : autosaveMeta}
        >
          {/* Wireframe R2/R3/R4/R5 — the secondary step-tab row. Note 7: "Step
              tabs, not a wizard. All four steps are reachable at any time — a
              requester filling their fifth request should not be forced
              through in order. Submit is gated on validation, not on step
              order." */}
          <div ref={stepRailRef} className={styles.stepRail}>
            <TabList selectedValue={String(step)} onTabSelect={handleTabSelect}>
              <Tab value="1">1. Details</Tab>
              <Tab value="2">2. Line items{rejectedStep === 2 ? ' 1 needed' : ''}</Tab>
              <Tab value="3">3. Attachments{rejectedStep === 3 ? ' 1 needed' : ''}</Tab>
              <Tab value="4">4. Review &amp; {rejection ? 'resubmit' : 'submit'}</Tab>
            </TabList>
          </div>
        </PageHeader>
      </div>

      {/* Wireframe R8 — pinned: no dismiss action, stays up for the whole edit.
          #155: when the reviewer named a section, R8's badge names it too, with
          a shortcut to the same section-focusing behaviour R5's per-section Edit
          buttons already use (`handleEdit*` below). */}
      {rejection && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Rejected by {rejection.actorName}</MessageBarTitle>
            {rejection.comment}
            {rejection.section && (
              <div className={styles.rejectionSectionRow}>
                <Badge appearance="outline" color="warning">
                  {REJECTION_SECTION_LABELS[rejection.section]}
                </Badge>
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => focusRejectedSection()}
                >
                  Go to this section
                </Button>
              </div>
            )}
          </MessageBarBody>
        </MessageBar>
      )}

      {autosaveStatus === 'error' && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save your draft</MessageBarTitle>
            We will keep retrying — do not close this tab.
          </MessageBarBody>
          {/* In `MessageBarActions`, never `MessageBarBody`: a `<Button>` inside
              the body sends Fluent into an infinite render loop (log entry 9). */}
          <MessageBarActions>
            <Button appearance="transparent" size="small" onClick={() => void runSave()}>
              Retry now
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {submitAttempted && !submitValidation.success && (
        <div
          ref={summaryRef}
          role="alert"
          aria-label="Form validation summary"
          tabIndex={-1}
          className={mergeClasses(surfaces.card, styles.summaryCard)}
        >
          {/* The red banner is a real `MessageBar` — palette colours are only
              ever permitted inside one — and the linked issues sit BESIDE it
              rather than in its body, because each is a `<Button>`. */}
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {issues.length} {issues.length === 1 ? 'thing needs' : 'things need'} fixing before
                this can be submitted.
              </MessageBarTitle>
            </MessageBarBody>
          </MessageBar>
          <ul className={styles.errorSummaryList}>
            {issues.map((issue) => (
              <li key={`${issue.path.join('.')}:${issue.message}`}>
                <Button appearance="transparent" onClick={() => focusField(issue.path)}>
                  {issue.message}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Layout (#206) — every step is FLUID: it takes the whole tab canvas,
          matching step 2's line-items table, rather than being capped at a
          measured reading width with a companion rail beside it. The rail
          used to restate the line-item count, attachment count and grand
          total that are already visible in the line-items table's own row
          count and Grand total row, and in the attachments list's own file
          count — so removing it drops no information, only the duplicate.
          Below 700px `responsive.singleColumn` still puts every field on its
          own row (S1c). */}
      {step === 2 ? (
        // Wireframe R3 — step 2, line items.
        <div className={layout.fluid}>
          <div id="section-lines" ref={lineItemsSectionRef} tabIndex={-1}>
            <Section level={2} title="Line items" count={lines.length}>
              <div className={styles.lineItemsStack}>
                {categories.state.phase === 'loading' && (
                  <div className={styles.hint}>
                    <Spinner
                      size="tiny"
                      label="Loading procurement categories…"
                      labelPosition="after"
                    />
                  </div>
                )}
                {categories.state.phase === 'error' && (
                  <Body1 as="p" className={styles.hint}>
                    Could not load procurement categories. Retrying automatically.{' '}
                    <Button appearance="transparent" size="small" onClick={categories.retry}>
                      Retry now
                    </Button>
                  </Body1>
                )}
                {units.state.phase === 'loading' && (
                  <div className={styles.hint}>
                    <Spinner size="tiny" label="Loading units of measure…" labelPosition="after" />
                  </div>
                )}
                {units.state.phase === 'error' && (
                  <Body1 as="p" className={styles.hint}>
                    Could not load units of measure. Retrying automatically.{' '}
                    <Button appearance="transparent" size="small" onClick={units.retry}>
                      Retry now
                    </Button>
                  </Body1>
                )}
                {categories.state.phase === 'ready' && categories.state.rows.length === 0 && (
                  <Body1 as="p" className={styles.hint}>
                    {noD365MatchHint('procurement categories')}
                  </Body1>
                )}
                {units.state.phase === 'ready' && units.state.rows.length === 0 && (
                  <Body1 as="p" className={styles.hint}>
                    {noD365MatchHint('units of measure')}
                  </Body1>
                )}

                {removedLine && (
                  <output
                    ref={undoToastRef}
                    aria-label="Undo line removal"
                    tabIndex={-1}
                    className={mergeClasses(surfaces.inset, styles.undoBanner)}
                  >
                    <Body1>Line removed.</Body1>
                    <Button appearance="transparent" size="small" onClick={undoRemoveLine}>
                      Undo
                    </Button>
                  </output>
                )}

                {/* Wireframe R3b — a brand-new draft (or one emptied by removing its
              last line, before the undo window above lapses) has no rows to
              show at all: the table/card layouts below assume at least one
              line, and rendering them empty is what used to produce a wall of
              "required" errors before the user had done anything (#248). */}
                {lines.length === 0 ? (
                  <EmptyState
                    variant="first-run"
                    title="No line items yet"
                    body="Add at least one line to describe what you're requesting."
                    action={
                      <Button id="field-lines-add-button" appearance="secondary" onClick={addLine}>
                        + Add first line
                      </Button>
                    }
                  />
                ) : (
                  <>
                    {/* Wireframe S1b — below the breakpoint the nine-column grid becomes a
            labelled card per line, with editing in a full-screen sheet. Both
            layouts drive the very same handlers. */}
                    {isNarrow ? (
                      <DataCardList ariaLabel="Line items">
                        {lines.map((line, index) => (
                          <LineItemCard
                            key={line.id}
                            line={line}
                            index={index}
                            lineCount={lines.length}
                            categories={categories.state}
                            units={units.state}
                            salesTaxGroups={salesTaxGroups.state}
                            currencyCode={selectedVendor?.currencyCode ?? ''}
                            touched={touchedByLine[line.id] ?? EMPTY_TOUCHED}
                            errors={lineErrorsFor(index)}
                            forceShowErrors={submitAttempted}
                            onEdit={() => setEditingLineId(line.id)}
                            onDuplicate={() => duplicateLine(line.id)}
                            onMoveUp={() => moveLine(line.id, -1)}
                            onMoveDown={() => moveLine(line.id, 1)}
                            onRemove={() => removeLine(line.id)}
                          />
                        ))}
                      </DataCardList>
                    ) : (
                      // The Delivery date and Total header cells below get an explicit
                      // `width` — see the `lineItemsDeliveryDateHeader`/
                      // `lineItemsTotalHeader` docblock. The outer scroll container is
                      // a defensive fallback for a total wide enough to still need
                      // more room than that (see `lineItemsTableScroll`'s docblock),
                      // never the primary fix.
                      <div className={styles.lineItemsTableScroll}>
                        <Table aria-label="Line items">
                          <TableHeader>
                            <TableRow>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsDescriptionHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Item description
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsCategoryHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Procurement category
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsQuantityHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Quantity
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsUnitHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Unit
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsNetPriceHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Unit price
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsDeliveryDateHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Delivery date
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsDeliveryDateHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Start date
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsDeliveryDateHeader,
                                  styles.alignCenter,
                                )}
                              >
                                End date
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsSalesTaxHeader,
                                  styles.alignCenter,
                                )}
                              >
                                Sales tax
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsTotalHeader,
                                  styles.stickyTotal,
                                  styles.alignCenter,
                                )}
                              >
                                Total
                              </TableHeaderCell>
                              <TableHeaderCell
                                className={mergeClasses(
                                  styles.lineItemsActionsHeader,
                                  styles.stickyActions,
                                  styles.alignCenter,
                                )}
                              >
                                Actions
                              </TableHeaderCell>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {lines.map((line, index) => (
                              <LineItemRow
                                key={line.id}
                                line={line}
                                index={index}
                                lineCount={lines.length}
                                categories={categories.state}
                                units={units.state}
                                salesTaxGroups={salesTaxGroups.state}
                                currencyCode={selectedVendor?.currencyCode ?? ''}
                                touched={touchedByLine[line.id] ?? EMPTY_TOUCHED}
                                errors={lineErrorsFor(index)}
                                forceShowErrors={submitAttempted}
                                onChange={(patch) => updateLine(line.id, patch)}
                                onTouch={(field) => touchLineField(line.id, field)}
                                onDuplicate={() => duplicateLine(line.id)}
                                onMoveUp={() => moveLine(line.id, -1)}
                                onMoveDown={() => moveLine(line.id, 1)}
                                onRemove={() => removeLine(line.id)}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {/* Wireframe S1b note 3 — the grand total belongs to the whole list,
              not to any one row, so it is a distinct surface pinned below the
              list in BOTH faces rather than a `<tfoot>` the mobile cards would
              lose. Same treatment the reviewer's read-only view already uses. */}
                    <div className={mergeClasses(surfaces.inset, styles.grandTotalRow)}>
                      <Body1Strong>Grand total</Body1Strong>
                      <Body1Strong className={styles.numeric}>
                        {formatTotal(grandTotal, selectedVendor?.currencyCode ?? '')}
                      </Body1Strong>
                    </div>

                    <Button
                      id="field-lines-add-button"
                      appearance="secondary"
                      onClick={(event) => {
                        // Take focus NOW, after the click has landed, not on mouse-down (below):
                        // the line sheet / row hands focus back to whatever opened it when it
                        // closes (K1), and if this button never held focus that is the page
                        // body — which leaves the page `aria-hidden` to assistive tech. A
                        // keyboard press already has focus here, so this is a no-op for it.
                        event.currentTarget.focus({ preventScroll: true });
                        addLine();
                      }}
                      // Never take focus on mouse-down: the new row's empty Item description is
                      // auto-focused, and blurring it reveals its "required" message, which grows
                      // the row and slides this button out from under the pointer — the click is
                      // lost and the person has to press it twice.
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      Add line
                    </Button>
                  </>
                )}
              </div>
            </Section>
          </div>
        </div>
      ) : (
        <div className={layout.fluid}>
          <div className={layout.stack}>
            {/* Wireframe R2 — step 1, five titled, bordered cards rather than one
          flat column; below the breakpoint every card (and every multi-field
          row inside it) collapses to a single column that fills the canvas. */}
            {step === 1 && (
              <div
                id="section-details"
                ref={detailsSectionRef}
                tabIndex={-1}
                className={mergeClasses(layout.stack, responsive.singleColumn)}
              >
                {/* R2 note 1/2 — purchase type and the company code that scopes every
            dependent field below it. */}
                <Section level={3} title="Purchase">
                  <div className={styles.cardStack}>
                    <Field label="Purchase type" required>
                      <RadioGroup
                        layout="horizontal"
                        value={purchaseType}
                        onChange={(_, data: RadioGroupOnChangeData) =>
                          setPurchaseType(data.value as PurchaseType)
                        }
                      >
                        {PURCHASE_TYPES.map((type) => (
                          <Radio
                            key={type}
                            value={type}
                            label={type === 'REGULAR' ? 'Regular' : 'Capital'}
                          />
                        ))}
                      </RadioGroup>
                      {changedHintFor(
                        'purchaseType',
                        purchaseType === 'CAPITAL' ? 'Capital' : 'Regular',
                      )}
                    </Field>

                    {purchaseType === 'CAPITAL' && (
                      <Field
                        label="Project ID"
                        required
                        validationState={showHeaderFieldError('projectId') ? 'error' : 'none'}
                        validationMessage={
                          showHeaderFieldError('projectId')
                            ? headerFieldMessage('projectId')
                            : undefined
                        }
                      >
                        <FilterableCombobox
                          id="field-projectId"
                          placeholder={
                            projects.state.phase === 'loading'
                              ? 'Loading…'
                              : projects.state.phase === 'error'
                                ? 'Unavailable — D365 not reachable'
                                : 'Select a project'
                          }
                          disabled={projects.state.phase !== 'ready'}
                          options={
                            projects.state.phase === 'ready'
                              ? projects.state.rows.map((row) => ({
                                  value: projectKey(row),
                                  label: projectLabel(row),
                                }))
                              : []
                          }
                          selectedValue={projectId || null}
                          fallbackLabel={resolveDependentLabel(
                            projects.state,
                            projectId || null,
                            projectKey,
                            projectLabel,
                          )}
                          onSelect={(value) => setProjectId(value ?? '')}
                          onBlur={() => touchHeaderField('projectId')}
                        />
                        {projects.state.phase === 'loading' && (
                          <div className={styles.hint}>
                            <Spinner size="tiny" label="Loading projects…" labelPosition="after" />
                          </div>
                        )}
                        {projects.state.phase === 'error' && (
                          <Body1 as="p" className={styles.hint}>
                            Unavailable — D365 not reachable. Retrying automatically.{' '}
                            <Button appearance="transparent" size="small" onClick={projects.retry}>
                              Retry now
                            </Button>
                          </Body1>
                        )}
                        {projects.state.phase === 'ready' && projects.state.rows.length === 0 && (
                          <Body1 as="p" className={styles.hint}>
                            {noD365MatchHint('projects')}
                          </Body1>
                        )}
                        {changedHintFor('projectId', projectId)}
                      </Field>
                    )}

                    {purchaseType === 'CAPITAL' && (
                      <div>
                        <DependentCombobox
                          id="field-projectCategoryId"
                          label="Project category"
                          company={company}
                          field={projectCategories}
                          getKey={(row) => row.code}
                          getLabel={(row) => codeLabel(row.code, row.description)}
                          selectedValue={projectCategoryId || null}
                          onSelect={(value) => setProjectCategoryId(value ?? '')}
                          errorMessage={undefined}
                          onBlur={() => touchHeaderField('projectCategoryId')}
                          required={isReviewMode}
                          clearable
                        />
                        <Body1 as="p" className={styles.hint}>
                          {isReviewMode
                            ? 'Required before this request can be approved.'
                            : 'Optional — your reviewer completes this if you leave it empty.'}
                        </Body1>
                        {changedHintFor('projectCategoryId', projectCategoryId)}
                      </div>
                    )}

                    <Field
                      label="Company code"
                      required
                      validationState={showHeaderFieldError('companyCode') ? 'error' : 'none'}
                      validationMessage={
                        showHeaderFieldError('companyCode')
                          ? headerFieldMessage('companyCode')
                          : undefined
                      }
                    >
                      <FilterableCombobox
                        id="field-companyCode"
                        onBlur={() => touchHeaderField('companyCode')}
                        placeholder={
                          companies.phase === 'loading' ? 'Loading…' : 'Select a company'
                        }
                        disabled={companies.phase !== 'ready'}
                        options={
                          companies.phase === 'ready'
                            ? companies.rows.map((c) => ({
                                value: c.LegalEntityId,
                                label: `${c.LegalEntityId} — ${c.Name}`,
                              }))
                            : []
                        }
                        selectedValue={company}
                        fallbackLabel={
                          company && companies.phase === 'ready'
                            ? (companies.rows.find((c) => c.LegalEntityId === company)?.Name ??
                              company)
                            : ''
                        }
                        onSelect={(value) => handleCompanySelect(value ?? undefined)}
                      />
                      {companies.phase === 'loading' && (
                        <div className={styles.hint}>
                          <Spinner size="tiny" label="Loading companies…" labelPosition="after" />
                        </div>
                      )}
                      {companies.phase === 'error' && (
                        <Body1 as="p" className={styles.hint}>
                          Could not load companies. Retrying automatically.{' '}
                          <Button appearance="transparent" size="small" onClick={retryCompanies}>
                            Retry now
                          </Button>
                        </Body1>
                      )}
                      {changedHintFor('companyCode', company ?? '')}
                    </Field>

                    {showCompanyChangeWarning && (
                      <Body1 as="p" className={styles.hint}>
                        Changing the company cleared cost centre, line of business, warehouse,
                        vendor and project — they don't apply to the new company.{' '}
                        <Button
                          appearance="transparent"
                          size="small"
                          onClick={() => setShowCompanyChangeWarning(false)}
                        >
                          Dismiss
                        </Button>
                      </Body1>
                    )}
                  </div>
                </Section>

                {/* R2 note 3 — cost centre, line of business, warehouse: all three
            scoped to the company above and disabled until it is set (R2c). */}
                <Section level={3} title="Financial dimensions">
                  <div className={styles.cardStack}>
                    {mainAccountRequired && (
                      <div>
                        <DependentCombobox
                          id="field-mainAccountId"
                          label="Main account"
                          company={company}
                          field={mainAccounts}
                          getKey={(row) => row.code}
                          getLabel={(row) => codeLabel(row.code, row.description)}
                          selectedValue={mainAccountId}
                          onSelect={(value) => setMainAccountId(value ?? null)}
                          errorMessage={headerFieldMessage('mainAccountId')}
                          onBlur={() => touchHeaderField('mainAccountId')}
                          required={isReviewMode}
                          clearable
                        />
                        <Body1 as="p" className={styles.hint}>
                          {isReviewMode
                            ? 'Required — the ledger account this request posts to.'
                            : 'Optional — your reviewer chooses it if you leave it empty.'}
                        </Body1>
                        {changedHintFor('mainAccountId', mainAccountId ?? '')}
                      </div>
                    )}
                    <div className={mergeClasses(styles.fieldRow, responsive.singleColumn)}>
                      <div className={styles.fieldCol}>
                        <DependentCombobox
                          id="field-costCentreId"
                          label="Cost centre"
                          company={company}
                          field={costCentres}
                          getKey={dimensionKey}
                          getLabel={dimensionLabel}
                          selectedValue={costCentreId}
                          onSelect={(value) => setCostCentreId(value ?? null)}
                          errorMessage={headerFieldMessage('costCentreId')}
                          onBlur={() => touchHeaderField('costCentreId')}
                          required={false}
                          clearable
                        />
                        {changedHintFor('costCentreId', costCentreId ?? '')}
                      </div>
                      <div className={styles.fieldCol}>
                        <DependentCombobox
                          id="field-lineOfBusinessId"
                          label="Line of business"
                          company={company}
                          field={lineOfBusiness}
                          getKey={dimensionKey}
                          getLabel={dimensionLabel}
                          selectedValue={lineOfBusinessId}
                          onSelect={(value) => setLineOfBusinessId(value ?? null)}
                          errorMessage={headerFieldMessage('lineOfBusinessId')}
                          onBlur={() => touchHeaderField('lineOfBusinessId')}
                          required={false}
                          clearable
                        />
                        {changedHintFor('lineOfBusinessId', lineOfBusinessId ?? '')}
                      </div>
                      <div className={styles.fieldCol}>
                        <DependentCombobox
                          id="field-warehouseId"
                          label="Warehouse"
                          company={company}
                          field={warehouses}
                          getKey={(row) => row.WarehouseId}
                          getLabel={(row) => `${row.WarehouseId} — ${row.WarehouseName}`}
                          selectedValue={warehouseId}
                          onSelect={(value) => setWarehouseId(value ?? null)}
                          errorMessage={headerFieldMessage('warehouseId')}
                          onBlur={() => touchHeaderField('warehouseId')}
                        />
                        {changedHintFor('warehouseId', warehouseId ?? '')}
                      </div>
                    </div>
                  </div>
                </Section>

                {/* R2 note 4 — the vendor search, and the read-only payment
            terms/currency it auto-fills once a vendor is picked. */}
                <Section level={3} title="Vendor">
                  <div className={styles.cardStack}>
                    <Field
                      label="Vendor"
                      required
                      validationState={showHeaderFieldError('vendorAccount') ? 'error' : 'none'}
                      validationMessage={
                        showHeaderFieldError('vendorAccount')
                          ? headerFieldMessage('vendorAccount')
                          : undefined
                      }
                    >
                      <Combobox
                        id="field-vendorAccount"
                        freeform
                        // Not disabled: the validation summary's "Vendor is
                        // required" link must still be able to focus it.
                        placeholder={
                          company
                            ? 'Search by name or account number (2+ characters)'
                            : 'Select a company first'
                        }
                        value={vendorQuery}
                        onChange={(event) => {
                          setVendorQuery(event.target.value);
                          setSelectedVendor(null);
                        }}
                        onOptionSelect={(_, data) => handleVendorSelect(data.optionValue)}
                        onBlur={() => touchHeaderField('vendorAccount')}
                      >
                        {vendorSearch.state.phase === 'ready' &&
                          vendorSearch.state.rows
                            .toSorted((a, b) => compareLabels(vendorLabel(a), vendorLabel(b)))
                            .map((row) => (
                              <Option
                                key={row.accountNumber}
                                value={row.accountNumber}
                                text={vendorLabel(row)}
                              >
                                {vendorLabel(row)} · {vendorDetail(row)}
                              </Option>
                            ))}
                      </Combobox>
                      {!company && vendorQuery.trim().length >= VENDOR_SEARCH_MIN_CHARS && (
                        <Body1 as="p" className={styles.hint}>
                          Select a company first — vendors are set up per company in D365, so the
                          search needs to know which one to look in.
                        </Body1>
                      )}
                      {vendorSearch.state.phase === 'loading' && (
                        <div className={styles.hint}>
                          <Spinner
                            size="tiny"
                            label="Searching D365 vendor master…"
                            labelPosition="after"
                          />
                        </div>
                      )}
                      {vendorSearch.state.phase === 'ready' &&
                        vendorSearch.state.rows.length === 0 && (
                          <Body1 as="p" className={styles.hint}>
                            No vendors match “{vendorSearch.debounced}”. Vendors come from the D365
                            vendor master and cannot be added here. Ask your D365 administrator to
                            create the vendor, then search again.
                          </Body1>
                        )}
                      {vendorSearch.state.phase === 'error' && (
                        <Body1 as="p" className={styles.hint}>
                          Could not search vendors. Retrying automatically.{' '}
                          <Button
                            appearance="transparent"
                            size="small"
                            onClick={vendorSearch.retry}
                          >
                            Retry now
                          </Button>
                        </Body1>
                      )}
                      {changedHintFor('vendorAccount', selectedVendor?.accountNumber ?? '')}
                    </Field>

                    {vendorCurrencyWarning && (
                      <MessageBar intent="warning">
                        <MessageBarBody>
                          <MessageBarTitle>
                            Line prices may be in the wrong currency
                          </MessageBarTitle>
                          {vendorCurrencyWarning}
                        </MessageBarBody>
                        <MessageBarActions>
                          <Button
                            appearance="transparent"
                            size="small"
                            onClick={() => setVendorCurrencyWarning(null)}
                          >
                            Dismiss
                          </Button>
                        </MessageBarActions>
                      </MessageBar>
                    )}
                  </div>
                </Section>

                {/* R2 note 5 — delivery date (required, seeds every new line's own
            delivery date). Term start/end were removed at the client's request. */}
                <Section level={3} title="Dates">
                  <div className={styles.cardStack}>
                    <div className={mergeClasses(styles.fieldRow, responsive.singleColumn)}>
                      <div className={styles.fieldCol}>
                        <Field
                          label="Delivery date"
                          required
                          validationState={showHeaderFieldError('deliveryDate') ? 'error' : 'none'}
                          validationMessage={
                            showHeaderFieldError('deliveryDate')
                              ? headerFieldMessage('deliveryDate')
                              : undefined
                          }
                        >
                          <DatePicker
                            id="field-deliveryDate"
                            value={deliveryDate}
                            minDate={startOfToday()}
                            onSelectDate={(date) => {
                              setDeliveryDate(date ?? null);
                              touchHeaderField('deliveryDate');
                            }}
                            onBlur={() => touchHeaderField('deliveryDate')}
                          />
                          {changedHintFor('deliveryDate', toIsoDate(deliveryDate) ?? '')}
                        </Field>
                      </div>
                    </div>
                  </div>
                </Section>

                {/* R2 note 6 — two note fields with different audiences; each says
            where it ends up. */}
                <Section level={3} title="Notes">
                  <div className={styles.cardStack}>
                    <div className={mergeClasses(styles.fieldRow, responsive.singleColumn)}>
                      <div className={styles.fieldCol}>
                        <Field label="Internal notes">
                          <Textarea
                            value={internalNotes}
                            maxLength={NOTES_MAX_LENGTH}
                            onChange={(_, data) =>
                              setInternalNotes(data.value.slice(0, NOTES_MAX_LENGTH))
                            }
                            resize="vertical"
                          />
                          <Body1 as="p" className={styles.charCount}>
                            {internalNotes.length} / {NOTES_MAX_LENGTH}
                          </Body1>
                          {changedHintFor('internalNotes', internalNotes)}
                        </Field>
                      </div>
                      <div className={styles.fieldCol}>
                        <Field label="Notes for vendor">
                          <Textarea
                            value={notesForVendor}
                            maxLength={NOTES_MAX_LENGTH}
                            onChange={(_, data) =>
                              setNotesForVendor(data.value.slice(0, NOTES_MAX_LENGTH))
                            }
                            resize="vertical"
                          />
                          <Body1 as="p" className={styles.charCount}>
                            {notesForVendor.length} / {NOTES_MAX_LENGTH}
                          </Body1>
                          {changedHintFor('notesForVendor', notesForVendor)}
                        </Field>
                      </div>
                    </div>
                  </div>
                </Section>
              </div>
            )}

            {/* Wireframe R4/R4a — step 3, attachments. */}
            {step === 3 && isReviewMode && (
              <div id="section-attachments" ref={attachmentsSectionRef} tabIndex={-1}>
                <MessageBar intent="info">
                  <MessageBarBody>Attachments cannot be changed while reviewing.</MessageBarBody>
                </MessageBar>
                {uploadedAttachments.map((attachment) => (
                  <Body1 key={attachment.id} as="p">
                    {attachment.fileName}
                  </Body1>
                ))}
              </div>
            )}
            {step === 3 && !isReviewMode && (
              <div id="section-attachments" ref={attachmentsSectionRef} tabIndex={-1}>
                <AttachmentsSection
                  ensureDraftId={ensureDraftId}
                  onAttachmentsChange={setUploadedAttachments}
                  onLocalFilesChange={setUploadedAttachmentLocalFiles}
                  initialAttachments={uploadedAttachments}
                />
              </div>
            )}

            {/* Wireframe R5/R8 — step 4, review & submit (or review & resubmit). */}
            {step === 4 && (
              <ReviewSummary
                isValid={submitValidation.success}
                details={reviewDetails}
                lines={reviewLines}
                grandTotal={grandTotal.toString()}
                currencyCode={selectedVendor?.currencyCode ?? ''}
                attachments={uploadedAttachments}
                localFiles={uploadedAttachmentLocalFiles}
                issues={submitAttempted ? reviewSummaryIssues : undefined}
                onEditDetails={handleEditDetails}
                onEditLines={handleEditLines}
                onEditAttachments={handleEditAttachments}
              />
            )}
          </div>
        </div>
      )}

      {/* Full-screen on mobile, and only ever rendered there: above the
          breakpoint the row itself is the editor. Deliberately not gated on
          `step` — an error-summary link on any step (or the K1 form-open
          rule) can name a line field and needs the sheet regardless of which
          step tab happens to be active. */}
      {isNarrow && editingLine !== undefined && editingLineIndex !== -1 && (
        <LineItemSheet
          line={editingLine}
          index={editingLineIndex}
          categories={categories.state}
          units={units.state}
          salesTaxGroups={salesTaxGroups.state}
          currencyCode={selectedVendor?.currencyCode ?? ''}
          errors={lineErrorsFor(editingLineIndex)}
          onChange={(patch) => updateLine(editingLine.id, patch)}
          onTouch={(field) => touchLineField(editingLine.id, field)}
          onClose={() => setEditingLineId(null)}
        />
      )}

      {submitError && (
        <MessageBar intent="error">
          <MessageBarBody>{submitError}</MessageBarBody>
        </MessageBar>
      )}

      {/* #42 "Done when": history shows both the rejection and the resubmission
          that follows it — every audit entry stays, insert-only, forever. */}
      {historySection}

      {/* Wireframe R2/R3/R4/R5 — each step has its own footer. Only step 1
          carries Close (renamed "Cancel" on the wireframe's own R2 sheet, but
          D1 — the authoritative spec for this exact button — names it "Close"
          and describes the behaviour below; kept as "Close" so the two never
          disagree, and so this is still the same button/ref/test-covered
          behaviour as before the wizard). Save & close appears on every step
          so a requester can always save and leave from wherever they are. */}
      {/* Exactly one `appearance="primary"` per row, and `ActionBar` is what
          guarantees it: the emphasised action is its own `primary` slot rather
          than something a caller composes. Close and Save & close go through
          `secondary` instead of the `back` slot because both need a `ref` —
          `handleFormBlur` reads them to decide NOT to start a background save
          when focus is on its way to a button that already owns that decision,
          and `back` is a `{ label, onClick }` object with nowhere to put one.

          `sticky` is E2 (`useSurfaceStyles().stickyBar`): on a twenty-field
          form the next step used to scroll out of reach entirely. K1 still
          holds — the bar is LAST in DOM order, so a keyboard user reads the
          step before reaching its primary action. */}
      {isReviewMode && (
        <ActionBar
          sticky
          back={{
            label: 'Cancel',
            onClick: () => void navigate(`/review?open=${draftIdRef.current ?? ''}`),
          }}
          primary={{ label: 'Save changes', onClick: () => void handleSaveReviewerEdits() }}
          busy={savingReview}
        />
      )}
      {!isReviewMode && step === 1 && (
        <ActionBar
          sticky
          secondary={
            <>
              <Button ref={closeButtonRef} appearance="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button
                ref={saveAndCloseButtonRef}
                appearance="secondary"
                onClick={() => void handleSaveAndClose()}
              >
                Save &amp; close
              </Button>
            </>
          }
          primary={{ label: 'Next: line items →', onClick: () => setStep(2) }}
        />
      )}
      {!isReviewMode && step === 2 && (
        <ActionBar
          sticky
          back={{ label: '← Back to details', onClick: () => setStep(1) }}
          secondary={
            <Button
              ref={saveAndCloseButtonRef}
              appearance="secondary"
              onClick={() => void handleSaveAndClose()}
            >
              Save &amp; close
            </Button>
          }
          primary={{ label: 'Next: attachments →', onClick: () => setStep(3) }}
        />
      )}
      {!isReviewMode && step === 3 && (
        <ActionBar
          sticky
          back={{ label: '← Back to line items', onClick: () => setStep(2) }}
          secondary={
            <Button
              ref={saveAndCloseButtonRef}
              appearance="secondary"
              onClick={() => void handleSaveAndClose()}
            >
              Save &amp; close
            </Button>
          }
          primary={{ label: 'Next: review & submit →', onClick: () => setStep(4) }}
        />
      )}
      {!isReviewMode && step === 4 && (
        <ActionBar
          sticky
          secondary={
            <Button
              ref={saveAndCloseButtonRef}
              appearance="secondary"
              onClick={() => void handleSaveAndClose()}
            >
              Save &amp; close
            </Button>
          }
          primary={{ label: 'Submit for review', onClick: handleSubmitClick }}
        />
      )}

      {/* D1: the only case where leaving the form can cost work — warn before letting it happen. */}
      <Dialog open={showCloseWarning} onOpenChange={(_, data) => setShowCloseWarning(data.open)}>
        <DialogSurface className={responsive.touchTargets}>
          <DialogBody>
            <DialogTitle>Your last change could not be saved</DialogTitle>
            <DialogContent>Close anyway, or retry?</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleCloseAnyway}>
                Close anyway
              </Button>
              <Button
                ref={retryFromCloseWarningRef}
                appearance="primary"
                onClick={handleRetryFromCloseWarning}
              >
                Retry
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
